import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { formatDisplayDate, convertFromTimestamp, getCurrentTimestamp, convertToISODate, convertFromISODate, prepareTimestampForFirebase } from '../utils/dateUtils';
import { TABLE_ICONS } from '../utils/tableIcons';
import Snackbar from '../components/Snackbar';
import { useSnackbar } from '../hooks/useSnackbar';
import './Uploads.css';

const Uploads = () => {
  const navigate = useNavigate();
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  // Helper function to safely trigger file input clicks
  const triggerFileInput = (selector) => {
    const input = document.querySelector(selector);
    if (input) {
      input.click();
    } else {
      console.warn(`File input with selector "${selector}" not found`);
    }
  };
  // Get the last active tab from localStorage, default to 'todayTomorrow' if not found
  const [activeCollection, setActiveCollection] = useState(() => {
    const savedTab = localStorage.getItem('activeUploadTab');
    return savedTab || 'todayTomorrow';
  });
  const [uploads, setUploads] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingUpload, setEditingUpload] = useState(null);
  const [formData, setFormData] = useState({
    category: '',
    startDate: '',
    endDate: ''
  });
  const [categoryFormData, setCategoryFormData] = useState({
    name: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreview, setFilePreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Today+Tomorrow specific states
  const [todayDates, setTodayDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDateForPicker, setSelectedDateForPicker] = useState('');
  const [showDateManagement, setShowDateManagement] = useState(false);
  const [subcategories, setSubcategories] = useState([]);
  const [dateUploads, setDateUploads] = useState([]);
  const [showSubcategoryForm, setShowSubcategoryForm] = useState(false);
  const [showMultiUploadForm, setShowMultiUploadForm] = useState(false);
  const [showEditDateForm, setShowEditDateForm] = useState(false);
  const [showEditSubcategoryForm, setShowEditSubcategoryForm] = useState(false);
  const [editingDate, setEditingDate] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [subcategoryFormData, setSubcategoryFormData] = useState({
    name: ''
  });
  const [multiUploadFormData, setMultiUploadFormData] = useState({
    subcategoryId: ''
  });
  const [editDateFormData, setEditDateFormData] = useState({
    date: ''
  });

  // Greetings specific states
  const [greetingSubcategories, setGreetingSubcategories] = useState([]);
  const [showGreetingManagement, setShowGreetingManagement] = useState(false);
  const [selectedGreetingSubcategory, setSelectedGreetingSubcategory] = useState(null);
  const [greetingUploads, setGreetingUploads] = useState([]);
  const [showGreetingSubcategoryForm, setShowGreetingSubcategoryForm] = useState(false);
  const [showGreetingUploadForm, setShowGreetingUploadForm] = useState(false);
  const [showEditGreetingSubcategoryForm, setShowEditGreetingSubcategoryForm] = useState(false);
  const [editingGreetingSubcategory, setEditingGreetingSubcategory] = useState(null);
  const [greetingSubcategoryFormData, setGreetingSubcategoryFormData] = useState({
    name: '',
    startDate: '',
    endDate: ''
  });
  const [greetingUploadFormData, setGreetingUploadFormData] = useState({
    subcategoryId: ''
  });
  const [showQuickGreetingUpload, setShowQuickGreetingUpload] = useState(false);
  const [quickUploadSubcategory, setQuickUploadSubcategory] = useState(null);
  const [quickUploadFiles, setQuickUploadFiles] = useState([]);

  // Trending specific states (similar to greetings)
  const [trendingSubcategories, setTrendingSubcategories] = useState([]);
  const [showTrendingManagement, setShowTrendingManagement] = useState(false);
  const [selectedTrendingSubcategory, setSelectedTrendingSubcategory] = useState(null);
  const [trendingUploads, setTrendingUploads] = useState([]);
  const [showTrendingSubcategoryForm, setShowTrendingSubcategoryForm] = useState(false);
  const [showTrendingUploadForm, setShowTrendingUploadForm] = useState(false);
  const [showEditTrendingSubcategoryForm, setShowEditTrendingSubcategoryForm] = useState(false);
  const [editingTrendingSubcategory, setEditingTrendingSubcategory] = useState(null);
  const [trendingSubcategoryFormData, setTrendingSubcategoryFormData] = useState({
    name: '',
    startDate: '',
    endDate: ''
  });
  const [trendingUploadFormData, setTrendingUploadFormData] = useState({
    subcategoryId: ''
  });
  const [showQuickTrendingUpload, setShowQuickTrendingUpload] = useState(false);
  const [showQuickTodayTomorrowUpload, setShowQuickTodayTomorrowUpload] = useState(false);

  const collections = [
    { key: 'todayTomorrow', label: 'Today+Tomorrow', icon: '📅' },
    { key: 'trending', label: 'Trending', icon: '🔥' },
    { key: 'greetings', label: 'Greetings', icon: '🎉' }
  ];

  useEffect(() => {
    if (activeCollection === 'todayTomorrow') {
      fetchTodayDates();
    } else if (activeCollection === 'greetings') {
      fetchGreetingSubcategories();
    } else if (activeCollection === 'trending') {
      fetchTrendingSubcategories();
    } else if (activeCollection === 'products') {
      fetchCategories();
    }
  }, [activeCollection]);
  useEffect(() => {
    if (selectedDate) {
      fetchSubcategories();
      fetchDateUploads();
    }
  }, [selectedDate]);

  useEffect(() => {
    if (selectedTrendingSubcategory) {
      fetchTrendingUploads(selectedTrendingSubcategory.id);
    }
  }, [selectedTrendingSubcategory]);

  // Helper function to format design count
  const formatDesignCount = (count) => {
    if (!count && count !== 0) return '0 designs';
    return count === 1 ? '1 design' : `${count} designs`;
  };

  // Helper function to create thumbnails for images or videos
  const createThumbnailForFile = async (file, maxSizeKB = 80) => {
    if (file.type.startsWith('image/')) {
      return await createThumbnail(file, maxSizeKB);
    } else if (file.type.startsWith('video/')) {
      return await createVideoThumbnail(file, maxSizeKB);
    } else {
      throw new Error('Unsupported file type for thumbnail creation');
    }
  };

  // Image compression utility to create thumbnails under 80KB
  const createThumbnail = async (file, maxSizeKB = 80) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        const maxDimension = 800; // Max width or height for thumbnail

        if (width > height) {
          if (width > maxDimension) {
            height = (height * maxDimension) / width;
            width = maxDimension;
          }
        } else {
          if (height > maxDimension) {
            width = (width * maxDimension) / height;
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress the image
        ctx.drawImage(img, 0, 0, width, height);

        // Start with quality 0.8 and reduce until under maxSizeKB
        let quality = 0.8;
        let compressedDataUrl;

        const compress = () => {
          compressedDataUrl = canvas.toDataURL('image/jpeg', quality);

          // Convert data URL to blob to check size
          const byteString = atob(compressedDataUrl.split(',')[1]);
          const sizeKB = byteString.length / 1024;

          if (sizeKB > maxSizeKB && quality > 0.1) {
            quality -= 0.1;
            compress();
          } else {
            // Convert data URL to File
            const byteNumbers = new Array(byteString.length);
            for (let i = 0; i < byteString.length; i++) {
              byteNumbers[i] = byteString.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const thumbnailFile = new File([byteArray], `thumb_${file.name}`, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve(thumbnailFile);
          }
        };

        compress();
      };

      img.src = URL.createObjectURL(file);
    });
  };

  // Video thumbnail generation utility
  const createVideoThumbnail = async (file, maxSizeKB = 80) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      video.preload = 'metadata';
      video.muted = true;

      video.onloadedmetadata = () => {
        // Seek to 1 second or 10% of video duration, whichever is smaller
        const seekTime = Math.min(1, video.duration * 0.1);
        video.currentTime = seekTime;
      };

      video.onseeked = () => {
        try {
          // Calculate thumbnail dimensions while maintaining aspect ratio
          let { videoWidth, videoHeight } = video;
          const maxDimension = 800; // Max width or height for thumbnail

          if (videoWidth > videoHeight) {
            if (videoWidth > maxDimension) {
              videoHeight = (videoHeight * maxDimension) / videoWidth;
              videoWidth = maxDimension;
            }
          } else {
            if (videoHeight > maxDimension) {
              videoWidth = (videoWidth * maxDimension) / videoHeight;
              videoHeight = maxDimension;
            }
          }

          canvas.width = videoWidth;
          canvas.height = videoHeight;

          // Draw video frame to canvas
          ctx.drawImage(video, 0, 0, videoWidth, videoHeight);

          // Compress the thumbnail
          let quality = 0.8;
          let compressedDataUrl;

          const compress = () => {
            compressedDataUrl = canvas.toDataURL('image/jpeg', quality);

            // Convert data URL to blob to check size
            const byteString = atob(compressedDataUrl.split(',')[1]);
            const sizeKB = byteString.length / 1024;

            if (sizeKB > maxSizeKB && quality > 0.1) {
              quality -= 0.1;
              compress();
            } else {
              // Convert data URL to File
              const byteNumbers = new Array(byteString.length);
              for (let i = 0; i < byteString.length; i++) {
                byteNumbers[i] = byteString.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              const thumbnailFile = new File([byteArray], `thumb_${file.name.split('.')[0]}.jpg`, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });

              // Clean up
              URL.revokeObjectURL(video.src);
              resolve(thumbnailFile);
            }
          };

          compress();
        } catch (error) {
          console.error('Error creating video thumbnail:', error);
          URL.revokeObjectURL(video.src);
          reject(error);
        }
      };

      video.onerror = (error) => {
        console.error('Video loading error:', error);
        URL.revokeObjectURL(video.src);
        reject(new Error('Failed to load video for thumbnail generation'));
      };

      video.src = URL.createObjectURL(file);
    });
  };

  // File validation helper
  const validateFiles = (files) => {
    const maxFileSize = 50 * 1024 * 1024; // 50MB for videos, 10MB for images
    const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const allowedVideoTypes = ['video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/flv', 'video/webm', 'video/mkv'];
    const errors = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const isImage = allowedImageTypes.includes(file.type);
      const isVideo = allowedVideoTypes.includes(file.type);

      // Check file type first
      if (!isImage && !isVideo) {
        errors.push(`${file.name}: Invalid file type. Only images (JPEG, PNG, GIF, WebP) and videos (MP4, AVI, MOV, WMV, FLV, WebM, MKV) are allowed`);
        continue;
      }

      // Check file size based on type
      const maxSizeForFile = isImage ? 10 * 1024 * 1024 : maxFileSize; // 10MB for images, 50MB for videos
      if (file.size > maxSizeForFile) {
        const maxSizeText = isImage ? '10MB' : '50MB';
        errors.push(`${file.name}: File size too large (max ${maxSizeText} for ${isImage ? 'images' : 'videos'})`);
      }
    }

    return errors;
  };

  const fetchUploads = async () => {
    try {
      setLoading(true);

      // Fetch all documents from the active collection
      const querySnapshot = await getDocs(collection(db, activeCollection));

      const uploadsList = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();

        // Only include documents that have the required fields
        if (data.createdAt && data.url) {
          uploadsList.push({
            id: doc.id,
            category: data.category || '',
            createdAt: data.createdAt,
            endDate: data.endDate || '',
            name: data.name || '',
            size: data.size || 0,
            startDate: data.startDate || '',
            storagePath: data.storagePath || '',
            type: data.type || '',
            url: data.url || ''
          });
        }
      });

      // Sort by createdAt (newest first)
      uploadsList.sort((a, b) => {
        const aDate = new Date(a.createdAt);
        const bDate = new Date(b.createdAt);
        return bDate - aDate;
      });

      setUploads(uploadsList);
    } catch (error) {
      console.error(`Error fetching ${activeCollection} uploads:`, error);
      setUploads([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {

      // Fetch all documents from the categories collection
      const categoryCollection = `${activeCollection}Categories`;
      const querySnapshot = await getDocs(collection(db, categoryCollection));

      const categoriesList = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();

        categoriesList.push({
          id: doc.id,
          name: data.name || '',
          createdAt: data.createdAt || ''
        });
      });

      // Sort by name
      categoriesList.sort((a, b) => a.name.localeCompare(b.name));

      setCategories(categoriesList);
    } catch (error) {
      console.error(`Error fetching ${activeCollection} categories:`, error);
      setCategories([]);
    }
  };

  // Today+Tomorrow specific functions
  const fetchTodayDates = async () => {
    try {
      setLoading(true);

      // Fetch all date documents from todayTomorrow collection
      const datesSnapshot = await getDocs(collection(db, 'todayTomorrow'));

      // Process all dates in parallel for faster loading
      const datesPromises = datesSnapshot.docs.map(async (dateDoc) => {
        const dateId = dateDoc.id;
        const dateData = dateDoc.data();

        // Count subcategories and uploads
        let subcategoryCount = 0;
        let designCount = 0;

        try {
          const subcategoriesSnapshot = await getDocs(collection(db, 'todayTomorrow', dateId, 'subcategories'));
          subcategoryCount = subcategoriesSnapshot.size;

          // Count uploads in each subcategory in parallel
          const uploadCountPromises = subcategoriesSnapshot.docs.map(async (subDoc) => {
            const uploadsSnapshot = await getDocs(collection(db, 'todayTomorrow', dateId, 'subcategories', subDoc.id, 'upload'));
            return uploadsSnapshot.size;
          });

          const uploadCounts = await Promise.all(uploadCountPromises);
          designCount = uploadCounts.reduce((sum, count) => sum + count, 0);
        } catch (error) {
          console.warn('Error counting for date:', dateId, error);
        }

        return {
          id: dateId,
          date: dateId, // Use document ID as date
          designCount: designCount,
          subcategoryCount: subcategoryCount,
          createdAt: dateData.createdAt || new Date().toISOString()
        };
      });

      const datesList = await Promise.all(datesPromises);

      // Sort by date (newest first) - handle dd-mm-yyyy format
      datesList.sort((a, b) => {
        const [dayA, monthA, yearA] = a.date.split('-').map(Number);
        const [dayB, monthB, yearB] = b.date.split('-').map(Number);
        const dateA = new Date(yearA, monthA - 1, dayA);
        const dateB = new Date(yearB, monthB - 1, dayB);
        return dateB - dateA;
      });

      // Limit to 15 most recent dates
      const limitedDatesList = datesList.slice(0, 15);

      setTodayDates(limitedDatesList);
    } catch (error) {
      console.error('Error fetching today dates:', error);
      setTodayDates([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubcategories = async () => {
    try {

      // Fetch subcategories from todayTomorrow/{date}/subcategories
      const subcategoriesSnapshot = await getDocs(collection(db, 'todayTomorrow', selectedDate, 'subcategories'));

      // Process all subcategories in parallel for faster loading
      const subcategoriesPromises = subcategoriesSnapshot.docs.map(async (docSnap) => {
        const data = docSnap.data();

        // Count uploads in the upload subcollection
        let designCount = 0;
        try {
          const uploadsSnapshot = await getDocs(collection(db, 'todayTomorrow', selectedDate, 'subcategories', docSnap.id, 'upload'));
          designCount = uploadsSnapshot.size;
        } catch (error) {
          console.warn('Error counting uploads for subcategory:', docSnap.id, error);
        }

        return {
          id: docSnap.id,
          categoryname: data.name || '',
          name: data.name || '',
          date: selectedDate,
          createdAt: data.createdAt || '',
          updatedAt: data.updatedAt || '',
          designCount: designCount
        };
      });

      const subcategoriesList = await Promise.all(subcategoriesPromises);

      // Sort by name
      subcategoriesList.sort((a, b) => a.categoryname.localeCompare(b.categoryname));

      setSubcategories(subcategoriesList);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      setSubcategories([]);
    }
  };

  const fetchDateUploads = async () => {
    try {

      const uploadsList = [];

      // Fetch all subcategories for this date
      const subcategoriesSnapshot = await getDocs(collection(db, 'todayTomorrow', selectedDate, 'subcategories'));

      // For each subcategory, fetch its uploads
      for (const subDoc of subcategoriesSnapshot.docs) {
        const subData = subDoc.data();
        const uploadsSnapshot = await getDocs(collection(db, 'todayTomorrow', selectedDate, 'subcategories', subDoc.id, 'upload'));

        uploadsSnapshot.forEach((uploadDoc) => {
          const uploadData = uploadDoc.data();

          uploadsList.push({
            id: uploadDoc.id,
            subcategoryId: subDoc.id,
            subcategoryName: subData.name || '',
            date: selectedDate,
            imageurl: uploadData.thumbnailImg || uploadData.downloadURL || uploadData.imageurl || '',
            downloadURL: uploadData.downloadURL || uploadData.imageurl || '',
            thumbnailImg: uploadData.thumbnailImg || '',
            fileName: uploadData.fileName || '',
            name: uploadData.name || '',
            createdAt: uploadData.createdAt || '',
            updatedAt: uploadData.updatedAt || '',
            fileSize: uploadData.fileSize || 0,
            fileType: uploadData.fileType || ''
          });
        });
      }

      // Sort by createdAt (newest first)
      uploadsList.sort((a, b) => {
        const aDate = new Date(a.createdAt);
        const bDate = new Date(b.createdAt);
        return bDate - aDate;
      });

      setDateUploads(uploadsList);
    } catch (error) {
      console.error('Error fetching date uploads:', error);
      setDateUploads([]);
    }
  };

  const handleDeleteDateUpload = async (uploadId, subcategoryId, imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      // Delete from Storage
      if (imageUrl) {
        try {
          const storageRef = ref(storage, imageUrl);
          await deleteObject(storageRef);
        } catch (storageError) {
          console.warn('Could not delete from storage:', storageError);
        }
      }

      // Delete from Firestore upload subcollection
      await deleteDoc(doc(db, 'todayTomorrow', selectedDate, 'subcategories', subcategoryId, 'upload', uploadId));

      // Update local state
      setDateUploads(dateUploads.filter(upload => upload.id !== uploadId));

      // Refresh subcategories to update counts
      await fetchSubcategories();

      showSnackbar('Image deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting date upload:', error);
      showSnackbar('Error deleting image. Please try again.', 'error');
    }
  };

  const handleAddDate = async (selectedDate) => {
    try {
      // Format date as dd-mm-yyyy
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const year = selectedDate.getFullYear();
      const dateString = `${day}-${month}-${year}`;

      // Check if date already exists
      const existingDate = todayDates.find(d => d.date === dateString);
      if (existingDate) {
        showSnackbar('Date already exists!', 'error');
        return;
      }

      const dateData = {
        createdAt: getCurrentTimestamp(),
        updatedAt: getCurrentTimestamp()
      };

      // Use date as document ID
      await setDoc(doc(db, 'todayTomorrow', dateString), dateData);

      const newDate = {
        id: dateString,
        date: dateString,
        ...dateData,
        designCount: 0,
        subcategoryCount: 0
      };

      // Limit to 15 dates maximum
      const updatedDates = [newDate, ...todayDates].sort((a, b) => {
        // Parse dd-mm-yyyy format for sorting
        const [dayA, monthA, yearA] = a.date.split('-').map(Number);
        const [dayB, monthB, yearB] = b.date.split('-').map(Number);
        const dateA = new Date(yearA, monthA - 1, dayA);
        const dateB = new Date(yearB, monthB - 1, dayB);
        return dateB - dateA;
      }).slice(0, 15); // Keep only the 15 most recent dates

      setTodayDates(updatedDates);
      setShowDatePicker(false);

      // Show appropriate success message based on whether we hit the limit
      if (todayDates.length >= 15) {
        showSnackbar('Date added successfully! Oldest date was removed to maintain 15-date limit.', 'success');
      } else {
        showSnackbar('Date added successfully!', 'success');
      }
    } catch (error) {
      console.error('Error adding date:', error);
      showSnackbar('Error adding date. Please try again.', 'error');
    }
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setShowDateManagement(true);
  };

  const handleAddSubcategory = async () => {
    try {
      const subcategoryData = {
        name: subcategoryFormData.name,
        createdAt: getCurrentTimestamp(),
        updatedAt: getCurrentTimestamp()
      };

      // Add to todayTomorrow/{date}/subcategories
      const docRef = await addDoc(collection(db, 'todayTomorrow', selectedDate, 'subcategories'), subcategoryData);

      const newSubcategory = {
        id: docRef.id,
        categoryname: subcategoryData.name,
        name: subcategoryData.name,
        date: selectedDate,
        createdAt: subcategoryData.createdAt,
        updatedAt: subcategoryData.updatedAt,
        designCount: 0
      };

      setSubcategories([...subcategories, newSubcategory].sort((a, b) => a.categoryname.localeCompare(b.categoryname)));
      setShowSubcategoryForm(false);
      setSubcategoryFormData({ name: '' });

      // Refresh subcategories to ensure consistency
      await fetchSubcategories();
    } catch (error) {
      console.error('Error adding subcategory:', error);
      alert('Error adding subcategory. Please try again.');
    }
  };

  const handleMultiUpload = async (files) => {
    // Validate files before upload
    const validationErrors = validateFiles(files);
    if (validationErrors.length > 0) {
      showSnackbar(`Upload failed: ${validationErrors.join(', ')}`, 'error');
      return;
    }

    try {
      setUploading(true);
      const totalFiles = files.length;

      // Upload all files in parallel for faster performance
      const uploadPromises = Array.from(files).map(async (file, index) => {
        const timestamp = Date.now() + index; // Add index to ensure unique timestamps
        const fileName = `${timestamp}_${file.name}`;
        const storageRef = ref(storage, `todayTomorrow/${selectedDate}/${multiUploadFormData.subcategoryId}/${fileName}`);

        // Create and upload thumbnail for both images and videos
        let thumbnailURL = null;
        let downloadURL;

        try {
          const thumbnailFile = await createThumbnailForFile(file);
          const thumbnailFileName = `thumb_${timestamp}_${file.name.split('.')[0]}.jpg`;
          const thumbnailStorageRef = ref(storage, `todayTomorrow/${selectedDate}/${multiUploadFormData.subcategoryId}/thumbnails/${thumbnailFileName}`);

          // Upload both original and thumbnail files
          const [snapshot, thumbnailSnapshot] = await Promise.all([
            uploadBytes(storageRef, file),
            uploadBytes(thumbnailStorageRef, thumbnailFile)
          ]);

          const [fileURL, thumbURL] = await Promise.all([
            getDownloadURL(snapshot.ref),
            getDownloadURL(thumbnailSnapshot.ref)
          ]);

          downloadURL = fileURL;
          thumbnailURL = thumbURL;

        } catch (error) {
          console.error('Error creating thumbnail:', error);
          // If thumbnail creation fails, still upload the original file
          const snapshot = await uploadBytes(storageRef, file);
          downloadURL = await getDownloadURL(snapshot.ref);

        }

        // Create upload data with thumbnail (keeping existing structure)
        const uploadData = {
          createdAt: getCurrentTimestamp(),
          name: file.name.split('.')[0],
          updatedAt: getCurrentTimestamp(),
          downloadURL: downloadURL,
          thumbnailImg: thumbnailURL,
          fileName: fileName,
          fileSize: file.size,
          fileType: file.type
        };

        // Add to the upload subcollection (keeping existing path structure)
        return addDoc(collection(db, 'todayTomorrow', selectedDate, 'subcategories', multiUploadFormData.subcategoryId, 'upload'), uploadData);
      });

      // Wait for all uploads to complete
      await Promise.all(uploadPromises);

      // Refresh the uploads and update design count
      await fetchDateUploads();
      await fetchTodayDates();

      setShowMultiUploadForm(false);
      setSelectedFiles([]);
      setMultiUploadFormData({ subcategoryId: '' });
      showSnackbar(`Successfully uploaded ${totalFiles} file(s)!`, 'success');
    } catch (error) {
      console.error('Error uploading files:', error);
      showSnackbar('Error uploading files. Please try again.', 'error');
    } finally {
      setUploading(false);
    }
  };

  // Preview functionality
  const handlePreviewDate = (date) => {
    navigate(`/admin/preview/todayTomorrow/${date}`);
  };

  const handlePreviewSubcategory = (subcategory) => {
    // Use the new todayTomorrow structure for preview
    // Pass both the date and subcategory ID using pipe separator
    navigate(`/admin/preview/todayTomorrowSubcategory/${selectedDate}|${subcategory.id}`);
  };

  // Delete individual upload
  const handleDeleteGreetingUpload = async (uploadId, imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      // Delete from Firestore upload subcollection
      await deleteDoc(doc(db, 'greetings', selectedGreetingSubcategory.id, 'upload', uploadId));

      // Delete from Storage
      if (imageUrl) {
        try {
          const storageRef = ref(storage, imageUrl);
          await deleteObject(storageRef);
        } catch (storageError) {
          console.warn('Could not delete from storage:', storageError);
        }
      }

      // Refresh uploads
      await fetchGreetingUploads(selectedGreetingSubcategory.id);
      await fetchGreetingSubcategories();

      showSnackbar('Image deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting greeting upload:', error);
      showSnackbar('Error deleting image. Please try again.', 'error');
    }
  };

  // Date management functions
  const handleEditDate = (dateItem) => {
    setEditingDate(dateItem);
    // Convert dd-MM-yyyy to yyyy-MM-dd for HTML date input
    setEditDateFormData({ date: convertToISODate(dateItem.date) });
    setShowEditDateForm(true);
  };

  const handleUpdateDate = async () => {
    try {
      // Convert yyyy-MM-dd from HTML input to dd-MM-yyyy format
      const newDateString = convertFromISODate(editDateFormData.date);

      if (!newDateString) {
        showSnackbar('Please enter a valid date!', 'warning');
        return;
      }

      // Check if new date already exists
      const existingDate = todayDates.find(d => d.date === newDateString);
      if (existingDate) {
        showSnackbar('This date already exists!', 'warning');
        return;
      }

      const oldDateId = editingDate.id;
      const newDateId = newDateString;

      // Check if the old document exists
      const oldDocRef = doc(db, 'todayTomorrow', oldDateId);
      const oldDocSnap = await getDoc(oldDocRef);

      if (!oldDocSnap.exists()) {
        showSnackbar('Original date document not found. Please refresh and try again.', 'error');
        return;
      }

      // Create new document with new date as ID
      const newDocRef = doc(db, 'todayTomorrow', newDateId);
      await setDoc(newDocRef, {
        createdAt: oldDocSnap.data().createdAt || getCurrentTimestamp(),
        updatedAt: getCurrentTimestamp()
      });

      // Copy all subcategories and their uploads to the new date document
      const subcategoriesSnapshot = await getDocs(collection(db, 'todayTomorrow', oldDateId, 'subcategories'));

      for (const subDoc of subcategoriesSnapshot.docs) {
        const subData = subDoc.data();
        const newSubRef = doc(db, 'todayTomorrow', newDateId, 'subcategories', subDoc.id);
        await setDoc(newSubRef, subData);

        // Copy all uploads in this subcategory
        const uploadsSnapshot = await getDocs(collection(db, 'todayTomorrow', oldDateId, 'subcategories', subDoc.id, 'upload'));

        for (const uploadDoc of uploadsSnapshot.docs) {
          const uploadData = uploadDoc.data();
          const newUploadRef = doc(db, 'todayTomorrow', newDateId, 'subcategories', subDoc.id, 'upload', uploadDoc.id);
          await setDoc(newUploadRef, uploadData);
        }
      }

      // Delete the old document and all its subcollections
      for (const subDoc of subcategoriesSnapshot.docs) {
        const uploadsSnapshot = await getDocs(collection(db, 'todayTomorrow', oldDateId, 'subcategories', subDoc.id, 'upload'));

        // Delete all uploads
        for (const uploadDoc of uploadsSnapshot.docs) {
          await deleteDoc(doc(db, 'todayTomorrow', oldDateId, 'subcategories', subDoc.id, 'upload', uploadDoc.id));
        }

        // Delete subcategory
        await deleteDoc(doc(db, 'todayTomorrow', oldDateId, 'subcategories', subDoc.id));
      }

      // Delete the old main document
      await deleteDoc(oldDocRef);

      // Refresh data
      await fetchTodayDates();

      setShowEditDateForm(false);
      setEditingDate(null);
      setEditDateFormData({ date: '' });
      showSnackbar('Date updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating date:', error);
      showSnackbar('Error updating date. Please try again.', 'error');
    }
  };

  const handleDeleteDate = (dateItem) => {
    setDeleteTarget({ type: 'date', item: dateItem });
    setShowDeleteConfirm(true);
  };

  const handleDeleteSubcategory = (subcategory) => {
    setDeleteTarget({ type: 'subcategory', item: subcategory });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      if (deleteTarget.type === 'date') {
        const dateItem = deleteTarget.item;
        const dateId = dateItem.id;

        // Delete all subcategories and their uploads for this date
        const subcategoriesSnapshot = await getDocs(collection(db, 'todayTomorrow', dateId, 'subcategories'));
        const deletePromises = [];

        for (const subDoc of subcategoriesSnapshot.docs) {
          // Delete all uploads in this subcategory
          const uploadsSnapshot = await getDocs(collection(db, 'todayTomorrow', dateId, 'subcategories', subDoc.id, 'upload'));

          uploadsSnapshot.forEach((uploadDoc) => {
            const uploadData = uploadDoc.data();
            // Delete from Storage
            if (uploadData.downloadURL) {
              try {
                const storageRef = ref(storage, uploadData.downloadURL);
                deletePromises.push(deleteObject(storageRef));
              } catch (error) {
                console.warn('Could not delete from storage:', error);
              }
            }
            // Delete from Firestore
            deletePromises.push(deleteDoc(doc(db, 'todayTomorrow', dateId, 'subcategories', subDoc.id, 'upload', uploadDoc.id)));
          });

          // Delete the subcategory document
          deletePromises.push(deleteDoc(doc(db, 'todayTomorrow', dateId, 'subcategories', subDoc.id)));
        }

        await Promise.all(deletePromises);

        // Delete the date document
        await deleteDoc(doc(db, 'todayTomorrow', dateId));

        // Refresh data
        await fetchTodayDates();

      } else if (deleteTarget.type === 'subcategory') {
        const subcategory = deleteTarget.item;

        // Delete all uploads from the upload subcollection
        const uploadsSnapshot = await getDocs(collection(db, 'todayTomorrow', selectedDate, 'subcategories', subcategory.id, 'upload'));
        const deletePromises = [];

        uploadsSnapshot.forEach((uploadDoc) => {
          const uploadData = uploadDoc.data();
          // Delete from Storage
          if (uploadData.downloadURL) {
            try {
              const storageRef = ref(storage, uploadData.downloadURL);
              deletePromises.push(deleteObject(storageRef));
            } catch (error) {
              console.warn('Could not delete from storage:', error);
            }
          }
          // Delete from Firestore
          deletePromises.push(deleteDoc(doc(db, 'todayTomorrow', selectedDate, 'subcategories', subcategory.id, 'upload', uploadDoc.id)));
        });

        await Promise.all(deletePromises);

        // Delete the subcategory document
        await deleteDoc(doc(db, 'todayTomorrow', selectedDate, 'subcategories', subcategory.id));

        // Refresh data
        await fetchSubcategories();
        await fetchDateUploads();
      } else if (deleteTarget.type === 'greetingSubcategory') {
        const subcategory = deleteTarget.item;

        // Delete all uploads from the upload subcollection
        const uploadsSnapshot = await getDocs(collection(db, 'greetings', subcategory.id, 'upload'));
        const deletePromises = [];

        uploadsSnapshot.forEach((uploadDoc) => {
          const uploadData = uploadDoc.data();
          // Delete from Storage
          if (uploadData.downloadURL) {
            try {
              const storageRef = ref(storage, uploadData.downloadURL);
              deletePromises.push(deleteObject(storageRef));
            } catch (error) {
              console.warn('Could not delete from storage:', error);
            }
          }
          // Delete from Firestore
          deletePromises.push(deleteDoc(doc(db, 'greetings', subcategory.id, 'upload', uploadDoc.id)));
        });

        await Promise.all(deletePromises);

        // Delete the greeting category document
        await deleteDoc(doc(db, 'greetings', subcategory.id));

        // Refresh data
        await fetchGreetingSubcategories();
        if (showGreetingManagement) {
          setShowGreetingManagement(false);
        }
      } else if (deleteTarget.type === 'trendingSubcategory') {
        const subcategory = deleteTarget.item;

        // Delete all uploads from the upload subcollection
        const uploadsSnapshot = await getDocs(collection(db, 'trending', subcategory.id, 'upload'));
        const deletePromises = [];

        uploadsSnapshot.forEach((uploadDoc) => {
          const uploadData = uploadDoc.data();
          // Delete from Storage
          if (uploadData.downloadURL) {
            try {
              const storageRef = ref(storage, uploadData.downloadURL);
              deletePromises.push(deleteObject(storageRef));
            } catch (error) {
              console.warn('Could not delete from storage:', error);
            }
          }
          // Delete from Firestore
          deletePromises.push(deleteDoc(doc(db, 'trending', subcategory.id, 'upload', uploadDoc.id)));
        });

        await Promise.all(deletePromises);

        // Delete the trending category document
        await deleteDoc(doc(db, 'trending', subcategory.id));

        // Refresh data
        await fetchTrendingSubcategories();
        if (showTrendingManagement) {
          setShowTrendingManagement(false);
        }
      }

      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      showSnackbar('Deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting:', error);
      showSnackbar('Error deleting. Please try again.', 'error');
    }
  };

  // Subcategory management functions
  const handleEditSubcategory = (subcategory) => {
    setEditingSubcategory(subcategory);
    setSubcategoryFormData({
      name: subcategory.categoryname
    });
    setShowEditSubcategoryForm(true);
  };
  const handleUpdateSubcategory = async () => {
    try {
      // Update in todayTomorrow/{date}/subcategories
      await updateDoc(doc(db, 'todayTomorrow', selectedDate, 'subcategories', editingSubcategory.id), {
        name: subcategoryFormData.name,
        updatedAt: getCurrentTimestamp()
      });

      // Refresh data
      await fetchSubcategories();

      setShowEditSubcategoryForm(false);
      setEditingSubcategory(null);
      setSubcategoryFormData({ name: '' });
      showSnackbar('Subcategory updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating subcategory:', error);
      showSnackbar('Error updating subcategory. Please try again.', 'error');
    }
  };

  // Greetings specific functions
  const fetchGreetingSubcategories = async () => {
    try {
      setLoading(true);

      // Test Firebase connection first
      try {
        const testQuery = await getDocs(collection(db, 'users'));

      } catch (testError) {
        console.error('🚨 Firebase connection test failed:', testError);
      }

      // Fetch all category documents from greetings collection
      const querySnapshot = await getDocs(collection(db, 'greetings'));

      if (querySnapshot.empty) {
        console.warn('⚠️ Greetings collection is empty!');
        setGreetingSubcategories([]);
        return;
      }

      // Process all categories in parallel for faster loading
      const subcategoriesPromises = querySnapshot.docs.map(async (docSnap) => {
        const data = docSnap.data();

        // Count uploads in the upload subcollection
        let designCount = 0;
        try {
          const uploadsSnapshot = await getDocs(collection(db, 'greetings', docSnap.id, 'upload'));
          designCount = uploadsSnapshot.size;
        } catch (error) {
          console.warn('Error counting uploads for category:', docSnap.id, error);
        }

        return {
          id: docSnap.id,
          categoryname: data.name || '',
          name: data.name || '',
          startDate: convertFromTimestamp(data.startDate) || '',
          endDate: convertFromTimestamp(data.endDate) || '',
          createdAt: convertFromTimestamp(data.createdAt) || '',
          updatedAt: convertFromTimestamp(data.updatedAt) || '',
          designCount: designCount
        };
      });

      const subcategoriesList = await Promise.all(subcategoriesPromises);

      // Sort by name
      subcategoriesList.sort((a, b) => a.categoryname.localeCompare(b.categoryname));

      setGreetingSubcategories(subcategoriesList);
    } catch (error) {
      console.error('❌ Error fetching greeting categories:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      setGreetingSubcategories([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGreetingUploads = async (subcategoryId) => {
    try {

      // Fetch from upload subcollection
      const uploadsSnapshot = await getDocs(collection(db, 'greetings', subcategoryId, 'upload'));

      const uploadsList = [];
      uploadsSnapshot.forEach((docSnap) => {
        const data = docSnap.data();

        uploadsList.push({
          id: docSnap.id,
          imageurl: data.thumbnailImg || data.downloadURL || data.imageurl || '',
          downloadURL: data.downloadURL || data.imageurl || '',
          thumbnailImg: data.thumbnailImg || '',
          fileName: data.fileName || '',
          fileSize: data.fileSize || 0,
          fileType: data.fileType || '',
          name: data.name || '',
          categoryId: subcategoryId,
          createdAt: data.createdAt || '',
          updatedAt: data.updatedAt || ''
        });
      });

      // Sort by createdAt (newest first)
      uploadsList.sort((a, b) => {
        const aDate = new Date(a.createdAt);
        const bDate = new Date(b.createdAt);
        return bDate - aDate;
      });

      setGreetingUploads(uploadsList);
    } catch (error) {
      console.error('Error fetching greeting uploads:', error);
      setGreetingUploads([]);
    }
  };

  const handleGreetingSubcategoryClick = (subcategory) => {
    // Open quick upload dialog instead of management interface
    setQuickUploadSubcategory(subcategory);
    setQuickUploadFiles([]);
    setShowQuickGreetingUpload(true);
  };

  const handleQuickGreetingUpload = async () => {
    if (!quickUploadFiles.length || !quickUploadSubcategory) {
      showSnackbar('Please select files to upload', 'warning');
      return;
    }

    // Validate files before upload
    const validationErrors = validateFiles(quickUploadFiles);
    if (validationErrors.length > 0) {
      showSnackbar(`Upload failed: ${validationErrors.join(', ')}`, 'error');
      return;
    }

    try {
      setUploading(true);

      // Upload all files in parallel for faster performance
      const uploadPromises = Array.from(quickUploadFiles).map(async (file, index) => {
        try {
          const timestamp = Date.now() + index; // Add index to ensure unique timestamps
          const fileName = `${timestamp}_${file.name}`;
          // Use organized storage path with subcategory
          const storageRef = ref(storage, `greetings/${quickUploadSubcategory.id}/${fileName}`);

          // Create and upload thumbnail for both images and videos
          let thumbnailURL = null;
          let downloadURL;

          try {
            const thumbnailFile = await createThumbnailForFile(file);
            const thumbnailFileName = `thumb_${timestamp}_${file.name.split('.')[0]}.jpg`;
            const thumbnailStorageRef = ref(storage, `greetings/${quickUploadSubcategory.id}/thumbnails/${thumbnailFileName}`);

            // Upload both original and thumbnail files
            const [snapshot, thumbnailSnapshot] = await Promise.all([
              uploadBytes(storageRef, file),
              uploadBytes(thumbnailStorageRef, thumbnailFile)
            ]);

            const [fileURL, thumbURL] = await Promise.all([
              getDownloadURL(snapshot.ref),
              getDownloadURL(thumbnailSnapshot.ref)
            ]);

            downloadURL = fileURL;
            thumbnailURL = thumbURL;

          } catch (error) {
            console.error('Error creating thumbnail:', error);
            // If thumbnail creation fails, still upload the original file
            const snapshot = await uploadBytes(storageRef, file);
            downloadURL = await getDownloadURL(snapshot.ref);

          }

          // Create upload data with thumbnail
          const uploadData = {
            createdAt: getCurrentTimestamp(),
            name: file.name.split('.')[0],
            updatedAt: getCurrentTimestamp(),
            downloadURL: downloadURL,
            thumbnailImg: thumbnailURL,
            fileName: fileName,
            fileSize: file.size,
            fileType: file.type,
            storagePath: `greetings/${quickUploadSubcategory.id}/${fileName}`
          };

          // Add to the upload subcollection of the selected category
          const docRef = await addDoc(collection(db, 'greetings', quickUploadSubcategory.id, 'upload'), uploadData);

          return docRef;
        } catch (fileError) {
          console.error(`Error uploading file ${file.name}:`, fileError);
          throw fileError;
        }
      });

      // Wait for all uploads to complete
      const results = await Promise.all(uploadPromises);

      // Refresh greetings subcategories to update counts
      await fetchGreetingSubcategories();

      setShowQuickGreetingUpload(false);
      setQuickUploadFiles([]);
      setQuickUploadSubcategory(null);

      showSnackbar(`Successfully uploaded ${quickUploadFiles.length} file(s) to ${quickUploadSubcategory.name}! Click "Preview" to view uploaded images.`, 'success');
    } catch (error) {
      console.error('Error uploading files:', error);
      showSnackbar(`Error uploading files: ${error.message}. Please try again.`, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleAddGreetingSubcategory = async () => {
    try {
      const categoryData = {
        name: greetingSubcategoryFormData.name,
        startDate: prepareTimestampForFirebase(greetingSubcategoryFormData.startDate),
        endDate: prepareTimestampForFirebase(greetingSubcategoryFormData.endDate),
        createdAt: getCurrentTimestamp(),
        updatedAt: getCurrentTimestamp()
      };

      const docRef = await addDoc(collection(db, 'greetings'), categoryData);

      const newSubcategory = {
        id: docRef.id,
        categoryname: categoryData.name,
        name: categoryData.name,
        startDate: categoryData.startDate,
        endDate: categoryData.endDate,
        createdAt: categoryData.createdAt,
        updatedAt: categoryData.updatedAt,
        designCount: 0
      };

      setGreetingSubcategories([...greetingSubcategories, newSubcategory].sort((a, b) => a.categoryname.localeCompare(b.categoryname)));
      setShowGreetingSubcategoryForm(false);
      setGreetingSubcategoryFormData({ name: '', startDate: '', endDate: '' });
      showSnackbar('Greeting category added successfully!', 'success');
    } catch (error) {
      console.error('Error adding greeting category:', error);
      showSnackbar('Error adding category. Please try again.', 'error');
    }
  };

  const handleGreetingMultiUpload = async (files) => {
    if (!selectedGreetingSubcategory) {
      showSnackbar('Please select a greeting category first', 'warning');
      return;
    }

    // Validate files before upload
    const validationErrors = validateFiles(files);
    if (validationErrors.length > 0) {
      showSnackbar(`Upload failed: ${validationErrors.join(', ')}`, 'error');
      return;
    }

    try {
      setUploading(true);

      // Upload all files in parallel for faster performance
      const uploadPromises = Array.from(files).map(async (file, index) => {
        try {
          const timestamp = Date.now() + index; // Add index to ensure unique timestamps
          const fileName = `${timestamp}_${file.name}`;
          // Use the same storage path structure as the quick upload
          const storageRef = ref(storage, `greetings/${selectedGreetingSubcategory.id}/${fileName}`);

          // Create and upload thumbnail for both images and videos
          let thumbnailURL = null;
          let downloadURL;

          try {
            const thumbnailFile = await createThumbnailForFile(file);
            const thumbnailFileName = `thumb_${timestamp}_${file.name.split('.')[0]}.jpg`;
            const thumbnailStorageRef = ref(storage, `greetings/${selectedGreetingSubcategory.id}/thumbnails/${thumbnailFileName}`);

            // Upload both original and thumbnail files
            const [snapshot, thumbnailSnapshot] = await Promise.all([
              uploadBytes(storageRef, file),
              uploadBytes(thumbnailStorageRef, thumbnailFile)
            ]);

            const [fileURL, thumbURL] = await Promise.all([
              getDownloadURL(snapshot.ref),
              getDownloadURL(thumbnailSnapshot.ref)
            ]);

            downloadURL = fileURL;
            thumbnailURL = thumbURL;

          } catch (error) {
            console.error('Error creating thumbnail:', error);
            // If thumbnail creation fails, still upload the original file
            const snapshot = await uploadBytes(storageRef, file);
            downloadURL = await getDownloadURL(snapshot.ref);

          }

          // Create upload data using the same structure as quick upload with thumbnail
          const uploadData = {
            createdAt: getCurrentTimestamp(),
            name: file.name.split('.')[0],
            updatedAt: getCurrentTimestamp(),
            downloadURL: downloadURL,
            thumbnailImg: thumbnailURL,
            fileName: fileName,
            fileSize: file.size,
            fileType: file.type,
            storagePath: `greetings/${selectedGreetingSubcategory.id}/${fileName}`
          };

          // Add to the upload subcollection of the selected category (same as quick upload)
          const docRef = await addDoc(collection(db, 'greetings', selectedGreetingSubcategory.id, 'upload'), uploadData);

          return docRef;
        } catch (fileError) {
          console.error(`Error uploading file ${file.name}:`, fileError);
          throw fileError;
        }
      });

      // Wait for all uploads to complete
      const results = await Promise.all(uploadPromises);

      // Refresh the uploads
      await fetchGreetingUploads(selectedGreetingSubcategory.id);
      await fetchGreetingSubcategories();

      setShowGreetingUploadForm(false);
      setSelectedFiles([]);
      setGreetingUploadFormData({ subcategoryId: '' });
      showSnackbar(`Successfully uploaded ${files.length} file(s) to ${selectedGreetingSubcategory.name}! Click "Preview" to view uploaded images.`, 'success');
    } catch (error) {
      console.error('Error uploading greeting files:', error);
      showSnackbar('Error uploading files. Please try again.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleGeneralGreetingUpload = async (files) => {
    const subcategoryId = greetingUploadFormData.subcategoryId;

    if (!subcategoryId) {
      showSnackbar('Please select a greeting category first', 'warning');
      return;
    }

    // Validate files before upload
    const validationErrors = validateFiles(files);
    if (validationErrors.length > 0) {
      showSnackbar(`Upload failed: ${validationErrors.join(', ')}`, 'error');
      return;
    }

    try {
      setUploading(true);

      // Find the selected subcategory details
      const selectedSubcategory = greetingSubcategories.find(sub => sub.id === subcategoryId);
      if (!selectedSubcategory) {
        showSnackbar('Selected greeting category not found', 'error');
        return;
      }

      // Upload all files in parallel for faster performance
      const uploadPromises = Array.from(files).map(async (file, index) => {
        try {
          const timestamp = Date.now() + index; // Add index to ensure unique timestamps
          const fileName = `${timestamp}_${file.name}`;
          // Use the same storage path structure as other upload methods
          const storageRef = ref(storage, `greetings/${subcategoryId}/${fileName}`);

          // Create and upload thumbnail (only for images)
          const thumbnailFile = await createThumbnailForFile(file);
          let thumbnailURL = null;
          let downloadURL;

          if (thumbnailFile) {
            const thumbnailFileName = `thumb_${timestamp}_${file.name}`;
            const thumbnailStorageRef = ref(storage, `greetings/${subcategoryId}/thumbnails/${thumbnailFileName}`);

            // Upload both original and thumbnail files
            const [snapshot, thumbnailSnapshot] = await Promise.all([
              uploadBytes(storageRef, file),
              uploadBytes(thumbnailStorageRef, thumbnailFile)
            ]);

            const [fileURL, thumbURL] = await Promise.all([
              getDownloadURL(snapshot.ref),
              getDownloadURL(thumbnailSnapshot.ref)
            ]);

            downloadURL = fileURL;
            thumbnailURL = thumbURL;
          } else {
            // For videos, only upload the original file
            const snapshot = await uploadBytes(storageRef, file);
            downloadURL = await getDownloadURL(snapshot.ref);
          }

          // Create upload data using the same structure as other methods with thumbnail
          const uploadData = {
            createdAt: getCurrentTimestamp(),
            name: file.name.split('.')[0],
            updatedAt: getCurrentTimestamp(),
            downloadURL: downloadURL,
            thumbnailImg: thumbnailURL,
            fileName: fileName,
            fileSize: file.size,
            fileType: file.type,
            storagePath: `greetings/${subcategoryId}/${fileName}`
          };

          // Add to the upload subcollection of the selected category
          const docRef = await addDoc(collection(db, 'greetings', subcategoryId, 'upload'), uploadData);

          return docRef;
        } catch (fileError) {
          console.error(`Error uploading file ${file.name}:`, fileError);
          throw fileError;
        }
      });

      // Wait for all uploads to complete
      const results = await Promise.all(uploadPromises);

      // Refresh the subcategories to update counts
      await fetchGreetingSubcategories();

      setShowGreetingUploadForm(false);
      setSelectedFiles([]);
      setGreetingUploadFormData({ subcategoryId: '' });
      showSnackbar(`Successfully uploaded ${files.length} file(s) to ${selectedSubcategory.name}! Click "Preview" to view uploaded images.`, 'success');
    } catch (error) {
      console.error('Error uploading greeting files:', error);
      showSnackbar('Error uploading files. Please try again.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleEditGreetingSubcategory = (subcategory) => {
    setEditingGreetingSubcategory(subcategory);
    setGreetingSubcategoryFormData({
      name: subcategory.categoryname,
      startDate: subcategory.startDate,
      endDate: subcategory.endDate
    });
    setShowEditGreetingSubcategoryForm(true);
  };

  const handleUpdateGreetingSubcategory = async () => {
    try {
      // Update in Firestore
      await updateDoc(doc(db, 'greetings', editingGreetingSubcategory.id), {
        name: greetingSubcategoryFormData.name,
        startDate: prepareTimestampForFirebase(greetingSubcategoryFormData.startDate),
        endDate: prepareTimestampForFirebase(greetingSubcategoryFormData.endDate),
        updatedAt: getCurrentTimestamp()
      });

      // Refresh data
      await fetchGreetingSubcategories();

      setShowEditGreetingSubcategoryForm(false);
      setEditingGreetingSubcategory(null);
      setGreetingSubcategoryFormData({ name: '', startDate: '', endDate: '' });
      showSnackbar('Greeting category updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating greeting category:', error);
      showSnackbar('Error updating category. Please try again.', 'error');
    }
  };

  const handleDeleteGreetingSubcategory = (subcategory) => {
    setDeleteTarget({ type: 'greetingSubcategory', item: subcategory });
    setShowDeleteConfirm(true);
  };

  const handlePreviewGreetingSubcategory = (subcategory) => {
    navigate(`/admin/preview/greetings/${subcategory.id}`);
  };

  // Trending specific functions (similar to greetings)
  const fetchTrendingSubcategories = async () => {
    try {
      setLoading(true);

      // Fetch all category documents from trending collection
      const querySnapshot = await getDocs(collection(db, 'trending'));

      // Process all categories in parallel for faster loading
      const subcategoriesPromises = querySnapshot.docs.map(async (docSnap) => {
        const data = docSnap.data();

        // Count uploads in the upload subcollection
        let designCount = 0;
        try {
          const uploadsSnapshot = await getDocs(collection(db, 'trending', docSnap.id, 'upload'));
          designCount = uploadsSnapshot.size;
        } catch (error) {
          console.warn('Error counting uploads for category:', docSnap.id, error);
        }

        return {
          id: docSnap.id,
          categoryname: data.name || '',
          name: data.name || '',
          startDate: convertFromTimestamp(data.startDate) || '',
          endDate: convertFromTimestamp(data.endDate) || '',
          createdAt: convertFromTimestamp(data.createdAt) || '',
          updatedAt: convertFromTimestamp(data.updatedAt) || '',
          designCount: designCount
        };
      });

      const subcategoriesList = await Promise.all(subcategoriesPromises);

      // Sort by name
      subcategoriesList.sort((a, b) => a.categoryname.localeCompare(b.categoryname));

      setTrendingSubcategories(subcategoriesList);
    } catch (error) {
      console.error('Error fetching trending categories:', error);
      setTrendingSubcategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTrendingSubcategoryClick = (subcategory) => {
    setSelectedTrendingSubcategory(subcategory);
    setShowTrendingManagement(true);
    fetchTrendingUploads(subcategory.id);
  };

  const fetchTrendingUploads = async (subcategoryId) => {
    try {

      // Fetch from upload subcollection
      const uploadsSnapshot = await getDocs(collection(db, 'trending', subcategoryId, 'upload'));

      const uploadsList = [];
      uploadsSnapshot.forEach((docSnap) => {
        const data = docSnap.data();

        uploadsList.push({
          id: docSnap.id,
          imageurl: data.thumbnailImg || data.downloadURL || data.imageurl || '',
          downloadURL: data.downloadURL || data.imageurl || '',
          thumbnailImg: data.thumbnailImg || '',
          fileName: data.fileName || '',
          fileSize: data.fileSize || 0,
          fileType: data.fileType || '',
          name: data.name || '',
          categoryId: subcategoryId,
          createdAt: data.createdAt || '',
          updatedAt: data.updatedAt || ''
        });
      });

      // Sort by createdAt (newest first)
      uploadsList.sort((a, b) => {
        const aDate = new Date(a.createdAt);
        const bDate = new Date(b.createdAt);
        return bDate - aDate;
      });

      setTrendingUploads(uploadsList);
    } catch (error) {
      console.error('Error fetching trending uploads:', error);
      setTrendingUploads([]);
    }
  };

  const handleAddTrendingSubcategory = async () => {
    try {
      const categoryData = {
        name: trendingSubcategoryFormData.name,
        startDate: prepareTimestampForFirebase(trendingSubcategoryFormData.startDate),
        endDate: prepareTimestampForFirebase(trendingSubcategoryFormData.endDate),
        createdAt: getCurrentTimestamp(),
        updatedAt: getCurrentTimestamp()
      };

      const docRef = await addDoc(collection(db, 'trending'), categoryData);

      const newSubcategory = {
        id: docRef.id,
        categoryname: categoryData.name,
        name: categoryData.name,
        startDate: categoryData.startDate,
        endDate: categoryData.endDate,
        createdAt: categoryData.createdAt,
        updatedAt: categoryData.updatedAt,
        designCount: 0
      };

      setTrendingSubcategories([...trendingSubcategories, newSubcategory].sort((a, b) => a.categoryname.localeCompare(b.categoryname)));
      setShowTrendingSubcategoryForm(false);
      setTrendingSubcategoryFormData({ name: '', startDate: '', endDate: '' });
      showSnackbar('Trending category added successfully!', 'success');
    } catch (error) {
      console.error('Error adding trending category:', error);
      showSnackbar('Error adding category. Please try again.', 'error');
    }
  };

  const handleEditTrendingSubcategory = (subcategory) => {
    setEditingTrendingSubcategory(subcategory);
    setTrendingSubcategoryFormData({
      name: subcategory.categoryname,
      startDate: subcategory.startDate,
      endDate: subcategory.endDate
    });
    setShowEditTrendingSubcategoryForm(true);
  };

  const handleUpdateTrendingSubcategory = async () => {
    try {
      // Update in Firestore
      await updateDoc(doc(db, 'trending', editingTrendingSubcategory.id), {
        name: trendingSubcategoryFormData.name,
        startDate: prepareTimestampForFirebase(trendingSubcategoryFormData.startDate),
        endDate: prepareTimestampForFirebase(trendingSubcategoryFormData.endDate),
        updatedAt: getCurrentTimestamp()
      });

      // Refresh data
      await fetchTrendingSubcategories();

      setShowEditTrendingSubcategoryForm(false);
      setEditingTrendingSubcategory(null);
      setTrendingSubcategoryFormData({ name: '', startDate: '', endDate: '' });
      showSnackbar('Trending category updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating trending category:', error);
      showSnackbar('Error updating category. Please try again.', 'error');
    }
  };

  const handleDeleteTrendingSubcategory = (subcategory) => {
    setDeleteTarget({ type: 'trendingSubcategory', item: subcategory });
    setShowDeleteConfirm(true);
  };

  const handleDeleteTrendingUpload = async (uploadId, imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      // Delete from Firestore upload subcollection
      await deleteDoc(doc(db, 'trending', selectedTrendingSubcategory.id, 'upload', uploadId));

      // Delete from Storage
      if (imageUrl) {
        try {
          const storageRef = ref(storage, imageUrl);
          await deleteObject(storageRef);
        } catch (storageError) {
          console.warn('Could not delete from storage:', storageError);
        }
      }

      // Refresh uploads
      await fetchTrendingUploads(selectedTrendingSubcategory.id);
      await fetchTrendingSubcategories();

      showSnackbar('Image deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting trending upload:', error);
      showSnackbar('Error deleting image. Please try again.', 'error');
    }
  };

  const handlePreviewTrendingSubcategory = (subcategory) => {
    navigate(`/admin/preview/trending/${subcategory.id}`);
  };

  const handleTrendingMultiUpload = async (files) => {
    if (!selectedTrendingSubcategory) {
      showSnackbar('Please select a trending category first', 'warning');
      return;
    }

    // Validate files before upload
    const validationErrors = validateFiles(files);
    if (validationErrors.length > 0) {
      showSnackbar(`Upload failed: ${validationErrors.join(', ')}`, 'error');
      return;
    }

    try {
      setUploading(true);

      // Upload all files in parallel for faster performance
      const uploadPromises = Array.from(files).map(async (file, index) => {
        try {
          const timestamp = Date.now() + index; // Add index to ensure unique timestamps
          const fileName = `${timestamp}_${file.name}`;
          // Use organized storage path by subcategory
          const storageRef = ref(storage, `trending/${selectedTrendingSubcategory.id}/${fileName}`);

          // Create and upload thumbnail for both images and videos
          let thumbnailURL = null;
          let downloadURL;

          try {
            const thumbnailFile = await createThumbnailForFile(file);
            const thumbnailFileName = `thumb_${timestamp}_${file.name.split('.')[0]}.jpg`;
            const thumbnailStorageRef = ref(storage, `trending/${selectedTrendingSubcategory.id}/thumbnails/${thumbnailFileName}`);

            // Upload both original and thumbnail files
            const [snapshot, thumbnailSnapshot] = await Promise.all([
              uploadBytes(storageRef, file),
              uploadBytes(thumbnailStorageRef, thumbnailFile)
            ]);

            const [fileURL, thumbURL] = await Promise.all([
              getDownloadURL(snapshot.ref),
              getDownloadURL(thumbnailSnapshot.ref)
            ]);

            downloadURL = fileURL;
            thumbnailURL = thumbURL;

          } catch (error) {
            console.error('Error creating thumbnail:', error);
            // If thumbnail creation fails, still upload the original file
            const snapshot = await uploadBytes(storageRef, file);
            downloadURL = await getDownloadURL(snapshot.ref);

          }

          // Create upload data using the same structure as greetings with thumbnail
          const uploadData = {
            createdAt: getCurrentTimestamp(),
            name: file.name.split('.')[0],
            updatedAt: getCurrentTimestamp(),
            downloadURL: downloadURL,
            thumbnailImg: thumbnailURL,
            fileName: fileName,
            fileSize: file.size,
            fileType: file.type,
            storagePath: `trending/${selectedTrendingSubcategory.id}/${fileName}`
          };

          // Add to the upload subcollection of the selected category
          const docRef = await addDoc(collection(db, 'trending', selectedTrendingSubcategory.id, 'upload'), uploadData);

          return docRef;
        } catch (fileError) {
          console.error(`Error uploading file ${file.name}:`, fileError);
          throw fileError;
        }
      });

      // Wait for all uploads to complete
      const results = await Promise.all(uploadPromises);

      // Refresh the uploads
      await fetchTrendingUploads(selectedTrendingSubcategory.id);
      await fetchTrendingSubcategories();

      // Clear selected files but keep the form open to show the updated preview
      setSelectedFiles([]);
      showSnackbar(`Successfully uploaded ${files.length} file(s) to ${selectedTrendingSubcategory.name}! Click "Preview" to view uploaded images.`, 'success');
    } catch (error) {
      console.error('Error uploading trending files:', error);
      showSnackbar(`Error uploading files: ${error.message}. Please try again.`, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleGeneralTrendingUpload = async (files) => {
    const subcategoryId = trendingUploadFormData.subcategoryId;

    if (!subcategoryId) {
      showSnackbar('Please select a trending category first', 'warning');
      return;
    }

    // Validate files before upload
    const validationErrors = validateFiles(files);
    if (validationErrors.length > 0) {
      showSnackbar(`Upload failed: ${validationErrors.join(', ')}`, 'error');
      return;
    }

    try {
      setUploading(true);

      // Find the selected subcategory details
      const selectedSubcategory = trendingSubcategories.find(sub => sub.id === subcategoryId);
      if (!selectedSubcategory) {
        showSnackbar('Selected trending category not found', 'error');
        return;
      }

      // Upload all files in parallel for faster performance
      const uploadPromises = Array.from(files).map(async (file, index) => {
        try {
          const timestamp = Date.now() + index; // Add index to ensure unique timestamps
          const fileName = `${timestamp}_${file.name}`;
          // Use organized storage path by subcategory
          const storageRef = ref(storage, `trending/${subcategoryId}/${fileName}`);

          // Create and upload thumbnail (only for images)
          const thumbnailFile = await createThumbnailForFile(file);
          let thumbnailURL = null;
          let downloadURL;

          if (thumbnailFile) {
            const thumbnailFileName = `thumb_${timestamp}_${file.name}`;
            const thumbnailStorageRef = ref(storage, `trending/${subcategoryId}/thumbnails/${thumbnailFileName}`);

            // Upload both original and thumbnail files
            const [snapshot, thumbnailSnapshot] = await Promise.all([
              uploadBytes(storageRef, file),
              uploadBytes(thumbnailStorageRef, thumbnailFile)
            ]);

            const [fileURL, thumbURL] = await Promise.all([
              getDownloadURL(snapshot.ref),
              getDownloadURL(thumbnailSnapshot.ref)
            ]);

            downloadURL = fileURL;
            thumbnailURL = thumbURL;
          } else {
            // For videos, only upload the original file
            const snapshot = await uploadBytes(storageRef, file);
            downloadURL = await getDownloadURL(snapshot.ref);
          }

          // Create upload data using the same structure as greetings with thumbnail
          const uploadData = {
            createdAt: getCurrentTimestamp(),
            name: file.name.split('.')[0],
            updatedAt: getCurrentTimestamp(),
            downloadURL: downloadURL,
            thumbnailImg: thumbnailURL,
            fileName: fileName,
            fileSize: file.size,
            fileType: file.type,
            storagePath: `trending/${subcategoryId}/${fileName}`
          };

          // Add to the upload subcollection of the selected category
          const docRef = await addDoc(collection(db, 'trending', subcategoryId, 'upload'), uploadData);

          return docRef;
        } catch (fileError) {
          console.error(`Error uploading file ${file.name}:`, fileError);
          throw fileError;
        }
      });

      // Wait for all uploads to complete
      const results = await Promise.all(uploadPromises);

      // Refresh the subcategories to update counts
      await fetchTrendingSubcategories();

      setShowTrendingUploadForm(false);
      setSelectedFiles([]);
      setTrendingUploadFormData({ subcategoryId: '' });
      showSnackbar(`Successfully uploaded ${files.length} file(s) to ${selectedSubcategory.name}! Click "Preview" to view uploaded images.`, 'success');
    } catch (error) {
      console.error('Error uploading trending files:', error);
      showSnackbar(`Error uploading files: ${error.message}. Please try again.`, 'error');
    } finally {
      setUploading(false);
    }
  };

  // Quick upload function for trending (similar to greetings)
  const handleQuickTrendingUpload = async () => {
    if (!quickUploadFiles.length || !quickUploadSubcategory) {
      showSnackbar('Please select files to upload', 'warning');
      return;
    }

    // Validate files before upload
    const validationErrors = validateFiles(quickUploadFiles);
    if (validationErrors.length > 0) {
      showSnackbar(`Upload failed: ${validationErrors.join(', ')}`, 'error');
      return;
    }

    try {
      setUploading(true);

      // Upload all files in parallel for faster performance
      const uploadPromises = Array.from(quickUploadFiles).map(async (file, index) => {
        try {
          const timestamp = Date.now() + index; // Add index to ensure unique timestamps
          const fileName = `${timestamp}_${file.name}`;
          // Use organized storage path by subcategory
          const storageRef = ref(storage, `trending/${quickUploadSubcategory.id}/${fileName}`);

          // Create and upload thumbnail for both images and videos
          let thumbnailURL = null;
          let downloadURL;

          try {
            const thumbnailFile = await createThumbnailForFile(file);
            const thumbnailFileName = `thumb_${timestamp}_${file.name.split('.')[0]}.jpg`;
            const thumbnailStorageRef = ref(storage, `trending/${quickUploadSubcategory.id}/thumbnails/${thumbnailFileName}`);

            // Upload both original and thumbnail files
            const [snapshot, thumbnailSnapshot] = await Promise.all([
              uploadBytes(storageRef, file),
              uploadBytes(thumbnailStorageRef, thumbnailFile)
            ]);

            const [fileURL, thumbURL] = await Promise.all([
              getDownloadURL(snapshot.ref),
              getDownloadURL(thumbnailSnapshot.ref)
            ]);

            downloadURL = fileURL;
            thumbnailURL = thumbURL;

          } catch (error) {
            console.error('Error creating thumbnail:', error);
            // If thumbnail creation fails, still upload the original file
            const snapshot = await uploadBytes(storageRef, file);
            downloadURL = await getDownloadURL(snapshot.ref);

          }

          // Create upload data using the same structure as greetings with thumbnail
          const uploadData = {
            createdAt: getCurrentTimestamp(),
            name: file.name.split('.')[0],
            updatedAt: getCurrentTimestamp(),
            downloadURL: downloadURL,
            thumbnailImg: thumbnailURL,
            fileName: fileName,
            fileSize: file.size,
            fileType: file.type,
            storagePath: `trending/${quickUploadSubcategory.id}/${fileName}`
          };

          // Add to the upload subcollection of the selected category
          const docRef = await addDoc(collection(db, 'trending', quickUploadSubcategory.id, 'upload'), uploadData);

          return docRef;
        } catch (fileError) {
          console.error(`Error uploading file ${file.name}:`, fileError);
          throw fileError;
        }
      });

      // Wait for all uploads to complete
      const results = await Promise.all(uploadPromises);

      // Refresh trending subcategories to update counts
      await fetchTrendingSubcategories();

      setShowQuickTrendingUpload(false);
      setQuickUploadFiles([]);
      setQuickUploadSubcategory(null);

      showSnackbar(`Successfully uploaded ${quickUploadFiles.length} file(s) to ${quickUploadSubcategory.name}! Click "Preview" to view uploaded images.`, 'success');
    } catch (error) {
      console.error('Error uploading files:', error);
      showSnackbar(`Error uploading files: ${error.message}. Please try again.`, 'error');
    } finally {
      setUploading(false);
    }
  };

  // Quick upload function for Today+Tomorrow (similar to greetings and trending)
  const handleQuickTodayTomorrowUpload = async () => {
    if (!quickUploadFiles.length || !quickUploadSubcategory) {
      showSnackbar('Please select files to upload', 'warning');
      return;
    }

    // Validate files before upload
    const validationErrors = validateFiles(quickUploadFiles);
    if (validationErrors.length > 0) {
      showSnackbar(`Upload failed: ${validationErrors.join(', ')}`, 'error');
      return;
    }

    try {
      setUploading(true);

      // Upload all files in parallel for faster performance
      const uploadPromises = Array.from(quickUploadFiles).map(async (file, index) => {
        try {
          const timestamp = Date.now() + index; // Add index to ensure unique timestamps
          const fileName = `${timestamp}_${file.name}`;
          // Use existing storage path structure for Today+Tomorrow
          const storageRef = ref(storage, `todayTomorrow/${quickUploadSubcategory.date}/${quickUploadSubcategory.id}/${fileName}`);

          // Create and upload thumbnail for both images and videos
          let thumbnailURL = null;
          let downloadURL;

          try {
            const thumbnailFile = await createThumbnailForFile(file);
            const thumbnailFileName = `thumb_${timestamp}_${file.name.split('.')[0]}.jpg`;
            const thumbnailStorageRef = ref(storage, `todayTomorrow/${quickUploadSubcategory.date}/${quickUploadSubcategory.id}/thumbnails/${thumbnailFileName}`);

            // Upload both original and thumbnail files
            const [snapshot, thumbnailSnapshot] = await Promise.all([
              uploadBytes(storageRef, file),
              uploadBytes(thumbnailStorageRef, thumbnailFile)
            ]);

            const [fileURL, thumbURL] = await Promise.all([
              getDownloadURL(snapshot.ref),
              getDownloadURL(thumbnailSnapshot.ref)
            ]);

            downloadURL = fileURL;
            thumbnailURL = thumbURL;

          } catch (error) {
            console.error('Error creating thumbnail:', error);
            // If thumbnail creation fails, still upload the original file
            const snapshot = await uploadBytes(storageRef, file);
            downloadURL = await getDownloadURL(snapshot.ref);

          }

          // Create upload data using existing Today+Tomorrow structure with thumbnail
          const uploadData = {
            createdAt: getCurrentTimestamp(),
            name: file.name.split('.')[0],
            updatedAt: getCurrentTimestamp(),
            downloadURL: downloadURL,
            thumbnailImg: thumbnailURL,
            fileName: fileName,
            fileSize: file.size,
            fileType: file.type
          };

          // Add to the upload subcollection using existing Today+Tomorrow path structure
          const docRef = await addDoc(collection(db, 'todayTomorrow', quickUploadSubcategory.date, 'subcategories', quickUploadSubcategory.id, 'upload'), uploadData);

          return docRef;
        } catch (fileError) {
          console.error(`Error uploading file ${file.name}:`, fileError);
          throw fileError;
        }
      });

      // Wait for all uploads to complete
      const results = await Promise.all(uploadPromises);

      // Refresh subcategories to update counts
      await fetchSubcategories(selectedDate);

      // Refresh main dates table to update design counts
      await fetchTodayDates();

      setShowQuickTodayTomorrowUpload(false);
      setQuickUploadFiles([]);
      setQuickUploadSubcategory(null);

      showSnackbar(`Successfully uploaded ${quickUploadFiles.length} file(s) to ${quickUploadSubcategory.categoryname}! Click "Preview" to view uploaded images.`, 'success');
    } catch (error) {
      console.error('Error uploading files:', error);
      showSnackbar(`Error uploading files: ${error.message}. Please try again.`, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleAddCategory = () => {
    setCategoryFormData({ name: '' });
    setShowCategoryForm(true);
  };

  const handleEdit = (upload) => {
    setEditingUpload(upload);
    setFormData({
      category: upload.category || '',
      startDate: upload.startDate || '',
      endDate: upload.endDate || ''
    });
    setSelectedFile(null);
    setFilePreview(upload.url || '');
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(`Are you sure you want to delete this ${activeCollection} item?`)) {
      try {
        await deleteDoc(doc(db, activeCollection, id));
        setUploads(uploads.filter(upload => upload.id !== id));
        showSnackbar('Item deleted successfully!', 'success');
      } catch (error) {
        console.error(`Error deleting ${activeCollection} item:`, error);
        showSnackbar('Error deleting item. Please try again.', 'error');
      }
    }
  };

  const uploadFile = async (file) => {
    if (!file) return null;

    try {
      setUploading(true);
      const timestamp = Date.now();
      const fileName = `${timestamp}-${file.name}`;
      const categoryId = formData.category || 'uncategorized';
      const storageRef = ref(storage, `${activeCollection}/${categoryId}/${fileName}`);

      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      return {
        url: downloadURL,
        name: file.name,
        size: file.size,
        type: file.type,
        storagePath: `${activeCollection}/${categoryId}/${fileName}`
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);

      let fileData = null;

      // Upload new file if selected
      if (selectedFile) {
        fileData = await uploadFile(selectedFile);
      } else if (editingUpload && editingUpload.url) {
        // Keep existing file data for edit mode
        fileData = {
          url: editingUpload.url,
          name: editingUpload.name,
          size: editingUpload.size,
          type: editingUpload.type,
          storagePath: editingUpload.storagePath
        };
      }

      // Only save the exact data structure specified
      const uploadData = {
        category: formData.category,
        createdAt: editingUpload ? editingUpload.createdAt : getCurrentTimestamp(),
        endDate: prepareTimestampForFirebase(formData.endDate),
        startDate: prepareTimestampForFirebase(formData.startDate),
        ...(fileData && {
          name: fileData.name,
          size: fileData.size,
          storagePath: fileData.storagePath,
          type: fileData.type,
          url: fileData.url
        })
      };

      if (editingUpload) {
        await updateDoc(doc(db, activeCollection, editingUpload.id), uploadData);
        setUploads(uploads.map(upload =>
          upload.id === editingUpload.id ? { ...upload, ...uploadData } : upload
        ));
      } else {
        const docRef = await addDoc(collection(db, activeCollection), uploadData);
        setUploads([{ id: docRef.id, ...uploadData }, ...uploads]);
      }

      setShowForm(false);
      setFormData({ category: '', startDate: '', endDate: '' });
      setSelectedFile(null);
      setFilePreview('');
      setEditingUpload(null);
      showSnackbar(`${activeCollection} item saved successfully!`, 'success');
    } catch (error) {
      console.error(`Error saving ${activeCollection} item:`, error);
      showSnackbar(`Error saving ${activeCollection} item. Please try again.`, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const categoryData = {
        name: categoryFormData.name,
        createdAt: getCurrentTimestamp()
      };

      const categoryCollection = `${activeCollection}Categories`;
      const docRef = await addDoc(collection(db, categoryCollection), categoryData);
      setCategories([...categories, { id: docRef.id, ...categoryData }].sort((a, b) => a.name.localeCompare(b.name)));

      setShowCategoryForm(false);
      setCategoryFormData({ name: '' });
      showSnackbar(`${activeCollection} category saved successfully!`, 'success');
    } catch (error) {
      console.error(`Error saving ${activeCollection} category:`, error);
      showSnackbar(`Error saving ${activeCollection} category. Please try again.`, 'error');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCategoryInputChange = (e) => {
    const { name, value } = e.target;
    setCategoryFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      const file = files[0];
      setSelectedFile(file);

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setFilePreview(e.target.result);
        reader.readAsDataURL(file);
      } else {
        setFilePreview('');
      }
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setFilePreview(e.target.result);
        reader.readAsDataURL(file);
      } else {
        setFilePreview('');
      }
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setFilePreview('');
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Unknown Category';
  };

  const getTrendingGroupedByCategory = () => {
    if (activeCollection !== 'trending') return [];

    const map = new Map();

    uploads.forEach(upload => {
      const categoryId = upload.category || 'uncategorized';
      if (!map.has(categoryId)) {
        map.set(categoryId, []);
      }
      map.get(categoryId).push(upload);
    });

    const groups = Array.from(map.entries()).map(([categoryId, items]) => ({
      categoryId,
      categoryName: getCategoryName(categoryId),
      items
    }));

    groups.sort((a, b) => a.categoryName.localeCompare(b.categoryName));
    groups.forEach(group => {
      group.items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    });

    return groups;
  };

  const handleCollectionChange = (collectionKey) => {
    setActiveCollection(collectionKey);
    // Save the active tab to localStorage
    localStorage.setItem('activeUploadTab', collectionKey);
    setUploads([]);
    setCategories([]);
    setShowForm(false);
    setShowCategoryForm(false);
    setEditingUpload(null);
  };

  if (loading) {
    return (
      <div className="uploads-loading">
        <div className="loading-spinner"></div>
        <p>Loading {collections.find(c => c.key === activeCollection)?.label}...</p>
      </div>
    );
  }

  const activeCollectionData = collections.find(c => c.key === activeCollection);

  return (
    <div className="uploads-page">
      {/* Collection Tabs */}
      <div className="collection-tabs">
        {collections.map(collection => (
          <button
            key={collection.key}
            className={`collection-tab ${activeCollection === collection.key ? 'active' : ''}`}
            onClick={() => handleCollectionChange(collection.key)}
          >
            <span className="tab-icon">{collection.icon}</span>
            <span className="tab-label">{collection.label}</span>
          </button>
        ))}
      </div>

      {/* Today+Tomorrow Date Management Interface */}
      {activeCollection === 'todayTomorrow' && !showDateManagement && (
        <>
          <div className="page-header today-tomorrow-header">
            <h2>📅 Today+Tomorrow Dates</h2>
            <button onClick={() => setShowDatePicker(true)} className="btn btn-primary today-add-date-btn">
              Add Date
            </button>
          </div>

          {/* Date Picker Modal */}
          {showDatePicker && (
            <div className="modal-overlay">
              <div className="modal">
                <div className="modal-header">
                  <h3>Select Date</h3>
                  <button
                    onClick={() => setShowDatePicker(false)}
                    className="close-btn"
                  >
                    ×
                  </button>
                </div>
                <div className="date-picker-form">
                  <div className="form-group">
                    <label>Select Date</label>
                    <input
                      type="date"
                      value={selectedDateForPicker || ''}
                      onChange={(e) => setSelectedDateForPicker(e.target.value)}
                      className="date-input"
                    />
                  </div>
                </div>
                <div className="form-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowDatePicker(false);
                      setSelectedDateForPicker('');
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      if (selectedDateForPicker) {
                        handleAddDate(new Date(selectedDateForPicker));
                        setSelectedDateForPicker('');
                      }
                    }}
                    disabled={!selectedDateForPicker}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Dates Table */}
          <div className="dates-table-container">
            <table className="dates-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Design Count</th>
                  <th>Add Design</th>
                  <th>Preview</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {todayDates.slice(0, 15).map(dateItem => (
                  <tr key={dateItem.id}>
                    <td>{formatDisplayDate(dateItem.date)}</td>
                    <td>{dateItem.designCount}</td>
                    <td>
                      <button
                        onClick={() => handleDateClick(dateItem.date)}
                        className="btn-icon"
                      >
                        {TABLE_ICONS.ADD}
                      </button>
                    </td>
                    <td>
                      <button
                        onClick={() => handlePreviewDate(dateItem.date)}
                        className="btn-icon"
                      >
                        {TABLE_ICONS.VIEW}
                      </button>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEditDate(dateItem)}
                          className="btn-icon"
                        >
                          {TABLE_ICONS.EDIT}
                        </button>
                        <button
                          onClick={() => handleDeleteDate(dateItem)}
                          className="btn-icon"
                        >
                          {TABLE_ICONS.DELETE}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Date Management Interface */}
      {activeCollection === 'todayTomorrow' && showDateManagement && (
        <>
          <div className="page-header">
            <div className="header-content">
              <h2>Create Subcategories for {formatDisplayDate(selectedDate)}</h2>
              <button
                onClick={() => setShowDateManagement(false)}
                className="btn btn-secondary back-btn"
              >
                ← Back to Dates
              </button>
            </div>
            <div className="header-actions">
              <button onClick={() => setShowSubcategoryForm(true)} className="btn btn-secondary">
                Create Subcategory
              </button>
              <button onClick={() => setShowMultiUploadForm(true)} className="btn btn-primary">
                + Add Design
              </button>
            </div>
          </div>

          {/* Subcategory Form Modal */}
          {showSubcategoryForm && (
            <div className="modal-overlay">
              <div className="modal">
                <div className="modal-header">
                  <h3>Create Subcategory for {formatDisplayDate(selectedDate)}</h3>
                  <button
                    onClick={() => setShowSubcategoryForm(false)}
                    className="close-btn"
                  >
                    ×
                  </button>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); handleAddSubcategory(); }} className="subcategory-form">
                  <div className="form-group">
                    <label>Subcategory Name *</label>
                    <input
                      type="text"
                      value={subcategoryFormData.name}
                      onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, name: e.target.value })}
                      required
                      placeholder="Enter subcategory name (e.g., Anant chaturthi)"
                    />
                  </div>
                  <div className="form-actions">
                    <button type="button" onClick={() => setShowSubcategoryForm(false)} className="btn btn-secondary">
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Create
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Multi Upload Form Modal */}
          {showMultiUploadForm && (
            <div className="modal-overlay">
              <div className="modal">
                <div className="modal-header">
                  <h3>Upload Designs</h3>
                  <button
                    onClick={() => setShowMultiUploadForm(false)}
                    className="close-btn"
                  >
                    ×
                  </button>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); handleMultiUpload(selectedFiles); }} className="multi-upload-form">
                  <div className="form-group">
                    <label>Subcategory *</label>
                    <select
                      value={multiUploadFormData.subcategoryId}
                      onChange={(e) => setMultiUploadFormData({ ...multiUploadFormData, subcategoryId: e.target.value })}
                      required
                    >
                      <option value="">Select Subcategory</option>
                      {subcategories.map(subcategory => (
                        <option key={subcategory.id} value={subcategory.id}>
                          {subcategory.categoryname}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Upload Images *</label>
                    <div
                      className={`multi-file-upload-area ${dragActive ? 'drag-active' : ''}`}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDragActive(false);
                        const files = Array.from(e.dataTransfer.files);
                        setSelectedFiles(files);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragActive(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setDragActive(false);
                      }}
                    >
                      {selectedFiles.length > 0 ? (
                        <div className="selected-files">
                          <p>{selectedFiles.length} files selected</p>
                          <div className="file-previews">
                            {selectedFiles.slice(0, 5).map((file, index) => (
                              <div key={index} className="file-preview-item">
                                {file.type.startsWith('image/') ? (
                                  <img
                                    src={URL.createObjectURL(file)}
                                    alt={file.name}
                                    className="preview-thumbnail"
                                  />
                                ) : file.type.startsWith('video/') ? (
                                  <video
                                    src={URL.createObjectURL(file)}
                                    className="preview-thumbnail"
                                    controls={false}
                                    muted
                                  />
                                ) : (
                                  <div className="file-icon">{TABLE_ICONS.FILE}</div>
                                )}
                                <span className="file-name">{file.name}</span>
                              </div>
                            ))}
                            {selectedFiles.length > 5 && (
                              <div className="more-files">+{selectedFiles.length - 5} more</div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="upload-placeholder">
                          <div className="upload-icon">{TABLE_ICONS.FOLDER}</div>
                          <p>Drag and drop multiple images/videos here, or click to select</p>
                          <input
                            type="file"
                            accept="image/*,video/*"
                            multiple
                            onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                            className="file-input"
                            style={{ display: 'none' }}
                          />
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => triggerFileInput('.file-input')}
                          >
                            Choose Files
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="button" onClick={() => setShowMultiUploadForm(false)} className="btn btn-secondary">
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedFiles([])}
                      className="btn btn-secondary"
                      disabled={selectedFiles.length === 0}
                    >
                      Clear All
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={uploading || selectedFiles.length === 0}>
                      {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} Files`}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Subcategories Table */}
          <div className="subcategories-table-container">
            <table className="subcategories-table">
              <thead>
                <tr>
                  <th>Subcategory</th>
                  <th>Date</th>
                  <th>Design Count</th>
                  <th>Add Design</th>
                  <th>Preview</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {subcategories.map(subcategory => {
                  return (
                    <tr key={subcategory.id}>
                      <td>{subcategory.categoryname}</td>
                      <td>{formatDisplayDate(subcategory.date || selectedDate)}</td>
                      <td>{subcategory.designCount || 0}</td>
                      <td>
                        <button
                          onClick={() => {

                            setQuickUploadSubcategory(subcategory);
                            setQuickUploadFiles([]);
                            setShowQuickTodayTomorrowUpload(true);
                          }}
                          className="btn-icon"
                          title="Quick Upload"
                        >
                          {TABLE_ICONS.ADD}
                        </button>
                      </td>
                      <td>
                        <button
                          onClick={() => handlePreviewSubcategory(subcategory)}
                          className="btn-icon"
                        >
                          {TABLE_ICONS.VIEW}
                        </button>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={() => handleEditSubcategory(subcategory)}
                            className="btn-icon"
                          >
                            {TABLE_ICONS.EDIT}
                          </button>
                          <button
                            onClick={() => handleDeleteSubcategory(subcategory)}
                            className="btn-icon"
                          >
                            {TABLE_ICONS.DELETE}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

        </>
      )}

      {/* Greetings Interface */}
      {activeCollection === 'greetings' && !showGreetingManagement && (
        <>
          <div className="page-header greetings-header">
            <h2>🎉 Greetings</h2>
            <div className="greetings-actions">
              <button onClick={() => setShowGreetingSubcategoryForm(true)} className="btn btn-primary">
                Add Greeting Subcategory
              </button>
              <button onClick={() => setShowGreetingUploadForm(true)} className="btn btn-secondary">
                Add Greeting Upload
              </button>
            </div>
          </div>

          {/* Greeting Subcategory Form Modal */}
          {showGreetingSubcategoryForm && (
            <div className="modal-overlay">
              <div className="modal">
                <div className="modal-header">
                  <h3>Create Greeting Subcategory</h3>
                  <button
                    onClick={() => setShowGreetingSubcategoryForm(false)}
                    className="close-btn"
                  >
                    ×
                  </button>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); handleAddGreetingSubcategory(); }} className="greeting-subcategory-form">
                  <div className="form-group">
                    <label>Subcategory Name *</label>
                    <input
                      type="text"
                      value={greetingSubcategoryFormData.name}
                      onChange={(e) => setGreetingSubcategoryFormData({ ...greetingSubcategoryFormData, name: e.target.value })}
                      required
                      placeholder="Enter subcategory name (e.g., वाढदिवस)"
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Start Date *</label>
                      <input
                        type="date"
                        value={greetingSubcategoryFormData.startDate}
                        onChange={(e) => setGreetingSubcategoryFormData({ ...greetingSubcategoryFormData, startDate: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>End Date *</label>
                      <input
                        type="date"
                        value={greetingSubcategoryFormData.endDate}
                        onChange={(e) => setGreetingSubcategoryFormData({ ...greetingSubcategoryFormData, endDate: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-actions">
                    <button type="button" onClick={() => setShowGreetingSubcategoryForm(false)} className="btn btn-secondary">
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Create
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Greetings Subcategories Table */}
          <div className="greetings-table-container">
            <table className="greetings-table">
              <thead>
                <tr>
                  <th>Subcategory</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Add Design</th>
                  <th>Preview</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {greetingSubcategories.map(subcategory => {
                  return (
                    <tr key={subcategory.id}>
                      <td>
                        <div className="subcategory-info">
                          <span className="subcategory-name">{subcategory.categoryname}</span>
                          <span className="design-count">({formatDesignCount(subcategory.designCount)})</span>
                        </div>
                      </td>
                      <td>{formatDisplayDate(subcategory.startDate)}</td>
                      <td>{formatDisplayDate(subcategory.endDate)}</td>
                      <td>
                        <button
                          onClick={() => handleGreetingSubcategoryClick(subcategory)}
                          className="btn-icon"
                        >
                          {TABLE_ICONS.ADD}
                        </button>
                      </td>
                      <td>
                        <button
                          onClick={() => handlePreviewGreetingSubcategory(subcategory)}
                          className="btn-icon"
                        >
                          {TABLE_ICONS.VIEW}
                        </button>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={() => handleEditGreetingSubcategory(subcategory)}
                            className="btn-icon"
                          >
                            {TABLE_ICONS.EDIT}
                          </button>
                          <button
                            onClick={() => handleDeleteGreetingSubcategory(subcategory)}
                            className="btn-icon"
                          >
                            {TABLE_ICONS.DELETE}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Quick Upload Dialog for Greetings */}
          {showQuickGreetingUpload && quickUploadSubcategory && (
            <div className="modal-overlay">
              <div className="modal">
                <div className="modal-header">
                  <h3>Quick Upload to {quickUploadSubcategory.categoryname || quickUploadSubcategory.name}</h3>
                  <button
                    onClick={() => {
                      setShowQuickGreetingUpload(false);
                      setQuickUploadSubcategory(null);
                      setQuickUploadFiles([]);
                    }}
                    className="close-btn"
                  >
                    ×
                  </button>
                </div>
                <div className="upload-modal-content">
                  <form onSubmit={(e) => { e.preventDefault(); handleQuickGreetingUpload(); }} className="quick-upload-form">
                    <div className="form-group">
                      <label>Upload Images *</label>
                      <div
                        className={`multi-file-upload-area ${dragActive ? 'drag-active' : ''}`}
                        onDrop={(e) => {
                          e.preventDefault();
                          setDragActive(false);
                          const files = Array.from(e.dataTransfer.files);
                          setQuickUploadFiles([...quickUploadFiles, ...files]);
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDragActive(true);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          setDragActive(false);
                        }}
                      >
                        {quickUploadFiles.length > 0 ? (
                          <div className="selected-files">
                            <p>{quickUploadFiles.length} files selected</p>
                            <div className="file-previews">
                              {quickUploadFiles.map((file, index) => (
                                <div key={index} className="file-preview-item">
                                  {file.type.startsWith('image/') ? (
                                    <img
                                      src={URL.createObjectURL(file)}
                                      alt={file.name}
                                      className="preview-thumbnail"
                                    />
                                  ) : (
                                    <div className="file-icon">{TABLE_ICONS.FILE}</div>
                                  )}
                                  <span className="file-name">{file.name}</span>
                                  <button
                                    type="button"
                                    className="remove-file-btn"
                                    onClick={() => {
                                      const newFiles = quickUploadFiles.filter((_, i) => i !== index);
                                      setQuickUploadFiles(newFiles);
                                    }}
                                    aria-label="Remove file"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                              <div className="add-more-files">
                                <input
                                  type="file"
                                  accept="image/*,video/*"
                                  multiple
                                  onChange={(e) => {
                                    const newFiles = Array.from(e.target.files);
                                    setQuickUploadFiles([...quickUploadFiles, ...newFiles]);
                                  }}
                                  className="file-input"
                                  id="additional-quick-greeting-files"
                                  style={{ display: 'none' }}
                                />
                                <label htmlFor="additional-quick-greeting-files" className="add-more-btn">
                                  + Add More
                                </label>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="upload-placeholder">
                            <div className="upload-icon">{TABLE_ICONS.FOLDER}</div>
                            <p>Drag and drop multiple images/videos here, or click to select</p>
                            <input
                              type="file"
                              accept="image/*,video/*"
                              multiple
                              onChange={(e) => setQuickUploadFiles(Array.from(e.target.files))}
                              className="file-input"
                              id="quick-greeting-upload-files"
                              style={{ display: 'none' }}
                            />
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => {
                                const input = document.getElementById('quick-greeting-upload-files');
                                if (input) input.click();
                              }}
                            >
                              Choose Files
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="form-actions">
                      <button type="button" onClick={() => {
                        setShowQuickGreetingUpload(false);
                        setQuickUploadSubcategory(null);
                        setQuickUploadFiles([]);
                      }} className="btn btn-secondary">
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => setQuickUploadFiles([])}
                        className="btn btn-secondary"
                        disabled={quickUploadFiles.length === 0}
                      >
                        Clear All
                      </button>
                      <button type="submit" className="btn btn-primary" disabled={uploading || quickUploadFiles.length === 0}>
                        {uploading ? 'Uploading...' : `Upload ${quickUploadFiles.length} Files`}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

        </>
      )}

      {/* Greeting Management Interface */}
      {activeCollection === 'greetings' && showGreetingManagement && (
        <>
          <div className="page-header">
            <div className="header-content">
              <h2>Manage {selectedGreetingSubcategory?.name} Uploads</h2>
              <button
                onClick={() => setShowGreetingManagement(false)}
                className="btn btn-secondary back-btn"
              >
                ← Back to Greetings
              </button>
            </div>
            <div className="header-actions">
              <button onClick={() => setShowGreetingUploadForm(true)} className="btn btn-primary">
                + Add Design
              </button>
            </div>
          </div>

          {/* Greeting Upload Form Modal - General Upload */}
          {showGreetingUploadForm && !showGreetingManagement && (
            <div className="modal-overlay">
              <div className="modal">
                <div className="modal-header">
                  <h3>Upload Greeting Designs</h3>
                  <button
                    onClick={() => setShowGreetingUploadForm(false)}
                    className="close-btn"
                  >
                    ×
                  </button>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); handleGeneralGreetingUpload(selectedFiles); }} className="greeting-upload-form">
                  <div className="form-group">
                    <label>Select Subcategory *</label>
                    <select
                      value={greetingUploadFormData.subcategoryId}
                      onChange={(e) => setGreetingUploadFormData({ ...greetingUploadFormData, subcategoryId: e.target.value })}
                      required
                    >
                      <option value="">Select Subcategory</option>
                      {greetingSubcategories.map(subcategory => (
                        <option key={subcategory.id} value={subcategory.id}>
                          {subcategory.categoryname}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Upload Images *</label>
                    <div
                      className={`multi-file-upload-area ${dragActive ? 'drag-active' : ''}`}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDragActive(false);
                        const files = Array.from(e.dataTransfer.files);
                        setSelectedFiles([...selectedFiles, ...files]);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragActive(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setDragActive(false);
                      }}
                    >
                      {selectedFiles.length > 0 ? (
                        <div className="selected-files">
                          <p>{selectedFiles.length} files selected</p>
                          <div className="file-previews">
                            {selectedFiles.map((file, index) => (
                              <div key={index} className="file-preview-item">
                                {file.type.startsWith('image/') ? (
                                  <img
                                    src={URL.createObjectURL(file)}
                                    alt={file.name}
                                    className="preview-thumbnail"
                                  />
                                ) : file.type.startsWith('video/') ? (
                                  <video
                                    src={URL.createObjectURL(file)}
                                    className="preview-thumbnail"
                                    controls={false}
                                    muted
                                  />
                                ) : (
                                  <div className="file-icon">{TABLE_ICONS.FILE}</div>
                                )}
                                <span className="file-name">{file.name}</span>
                                <button
                                  type="button"
                                  className="remove-file-btn"
                                  onClick={() => {
                                    const newFiles = selectedFiles.filter((_, i) => i !== index);
                                    setSelectedFiles(newFiles);
                                  }}
                                  aria-label="Remove file"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                            <div className="add-more-files">
                              <input
                                type="file"
                                accept="image/*,video/*"
                                multiple
                                onChange={(e) => {
                                  const newFiles = Array.from(e.target.files);
                                  setSelectedFiles([...selectedFiles, ...newFiles]);
                                }}
                                className="file-input"
                                id="additional-greeting-files-1"
                                style={{ display: 'none' }}
                              />
                              <label htmlFor="additional-greeting-files-1" className="add-more-btn">
                                + Add More
                              </label>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="upload-placeholder">
                          <div className="upload-icon">{TABLE_ICONS.FOLDER}</div>
                          <p>Drag and drop multiple images/videos here, or click to select</p>
                          <input
                            type="file"
                            accept="image/*,video/*"
                            multiple
                            onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                            className="file-input"
                            style={{ display: 'none' }}
                          />
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => triggerFileInput('.file-input')}
                          >
                            Choose Files
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="button" onClick={() => setShowGreetingUploadForm(false)} className="btn btn-secondary">
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedFiles([])}
                      className="btn btn-secondary"
                      disabled={selectedFiles.length === 0}
                    >
                      Clear All
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={uploading || selectedFiles.length === 0}>
                      {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} Files`}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Greeting Upload Form Modal - Specific Subcategory */}
          {showGreetingUploadForm && showGreetingManagement && (
            <div className="modal-overlay">
              <div className="modal">
                <div className="modal-header">
                  <h3>Upload Greeting Designs</h3>
                  <button
                    onClick={() => setShowGreetingUploadForm(false)}
                    className="close-btn"
                  >
                    ×
                  </button>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); handleGreetingMultiUpload(selectedFiles); }} className="greeting-upload-form">
                  <div className="form-group">
                    <label>Upload Images *</label>
                    <div
                      className={`multi-file-upload-area ${dragActive ? 'drag-active' : ''}`}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDragActive(false);
                        const files = Array.from(e.dataTransfer.files);
                        setSelectedFiles(files);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragActive(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setDragActive(false);
                      }}
                    >
                      {selectedFiles.length > 0 ? (
                        <div className="selected-files">
                          <p>{selectedFiles.length} files selected</p>
                          <div className="file-previews">
                            {selectedFiles.slice(0, 5).map((file, index) => (
                              <div key={index} className="file-preview-item">
                                {file.type.startsWith('image/') ? (
                                  <img
                                    src={URL.createObjectURL(file)}
                                    alt={file.name}
                                    className="preview-thumbnail"
                                  />
                                ) : file.type.startsWith('video/') ? (
                                  <video
                                    src={URL.createObjectURL(file)}
                                    className="preview-thumbnail"
                                    controls={false}
                                    muted
                                  />
                                ) : (
                                  <div className="file-icon">{TABLE_ICONS.FILE}</div>
                                )}
                                <span className="file-name">{file.name}</span>
                              </div>
                            ))}
                            {selectedFiles.length > 5 && (
                              <div className="more-files">+{selectedFiles.length - 5} more</div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => setSelectedFiles([])}
                            className="btn btn-sm btn-secondary"
                          >
                            Clear All
                          </button>
                        </div>
                      ) : (
                        <div className="upload-placeholder">
                          <div className="upload-icon">{TABLE_ICONS.FOLDER}</div>
                          <p>Drag and drop multiple images/videos here, or click to select</p>
                          <input
                            type="file"
                            accept="image/*,video/*"
                            multiple
                            onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                            className="file-input"
                            style={{ display: 'none' }}
                          />
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => triggerFileInput('.file-input')}
                          >
                            Choose Files
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="button" onClick={() => setShowGreetingUploadForm(false)} className="btn btn-secondary">
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={uploading || selectedFiles.length === 0}>
                      {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} Files`}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Greeting Uploads Grid */}
          <div className="greeting-uploads-grid">
            {greetingUploads.length === 0 ? (
              <div className="no-uploads">
                <div className="no-uploads-icon">🎉</div>
                <h3>No Greeting Images Found</h3>
                <p>Upload some greeting images to get started!</p>
                <button onClick={() => setShowGreetingUploadForm(true)} className="btn btn-primary">
                  Upload First Image
                </button>
              </div>
            ) : (
              <div className="images-grid">
                {greetingUploads.map(upload => (
                  <div key={upload.id} className="image-card">
                    <div className="image-container">
                      <img src={upload.imageurl} alt={`Upload ${upload.id}`} />
                      <button
                        onClick={() => handleDeleteGreetingUpload(upload.id, upload.imageurl)}
                        className="delete-image-btn"
                        title="Delete Image"
                      >
                        {TABLE_ICONS.DELETE}
                      </button>
                    </div>
                    <div className="image-info">
                      <h4>{upload.name}</h4>
                      <p className="image-date">{formatDisplayDate(upload.createdAt)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Edit Greeting Subcategory Form Modal */}
      {showEditGreetingSubcategoryForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit Greeting Subcategory</h3>
              <button
                onClick={() => setShowEditGreetingSubcategoryForm(false)}
                className="close-btn"
              >
                ×
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handleUpdateGreetingSubcategory(); }} className="edit-greeting-subcategory-form">
              <div className="form-group">
                <label>Subcategory Name *</label>
                <input
                  type="text"
                  value={greetingSubcategoryFormData.name}
                  onChange={(e) => setGreetingSubcategoryFormData({ ...greetingSubcategoryFormData, name: e.target.value })}
                  required
                  placeholder="Enter subcategory name"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    value={greetingSubcategoryFormData.startDate}
                    onChange={(e) => setGreetingSubcategoryFormData({ ...greetingSubcategoryFormData, startDate: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>End Date *</label>
                  <input
                    type="date"
                    value={greetingSubcategoryFormData.endDate}
                    onChange={(e) => setGreetingSubcategoryFormData({ ...greetingSubcategoryFormData, endDate: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowEditGreetingSubcategoryForm(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Update Subcategory
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Date Form Modal */}
      {showEditDateForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit Date</h3>
              <button
                onClick={() => setShowEditDateForm(false)}
                className="close-btn"
              >
                ×
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handleUpdateDate(); }} className="edit-date-form">
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  value={editDateFormData.date}
                  onChange={(e) => setEditDateFormData({ ...editDateFormData, date: e.target.value })}
                  required
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowEditDateForm(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Update Date
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Subcategory Form Modal */}
      {showEditSubcategoryForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit Subcategory for {formatDisplayDate(selectedDate)}</h3>
              <button
                onClick={() => setShowEditSubcategoryForm(false)}
                className="close-btn"
              >
                ×
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handleUpdateSubcategory(); }} className="edit-subcategory-form">
              <div className="form-group">
                <label>Subcategory Name *</label>
                <input
                  type="text"
                  value={subcategoryFormData.name}
                  onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, name: e.target.value })}
                  required
                  placeholder="Enter subcategory name"
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowEditSubcategoryForm(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Update Subcategory
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="close-btn"
              >
                ×
              </button>
            </div>
            <div className="delete-confirm-content">
              <div className="warning-icon">{TABLE_ICONS.WARNING}</div>
              <p>
                Are you sure you want to delete this {deleteTarget?.type === 'greetingSubcategory' ? 'greeting subcategory' : deleteTarget?.type === 'trendingSubcategory' ? 'trending subcategory' : deleteTarget?.type}?
                {deleteTarget?.type === 'date' && ' This will also delete all uploads for this date.'}
                {deleteTarget?.type === 'subcategory' && ' This will also delete all uploads in this subcategory.'}
                {deleteTarget?.type === 'greetingSubcategory' && ' This will also delete all uploads in this subcategory.'}
                {deleteTarget?.type === 'trendingSubcategory' && ' This will also delete all uploads in this subcategory.'}
              </p>
              <p className="warning-text">This action cannot be undone.</p>
              <div className="form-actions">
                <button onClick={() => setShowDeleteConfirm(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button onClick={confirmDelete} className="btn btn-danger">
                  Delete {deleteTarget?.type === 'date' ? 'Date' : 'Subcategory'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trending Interface */}
      {activeCollection === 'trending' && !showTrendingManagement && (
        <>
          <div className="page-header trending-header">
            <h2>🔥 Trending</h2>
            <div className="trending-actions">
              <button onClick={() => setShowTrendingSubcategoryForm(true)} className="btn btn-primary">
                Add Trending Subcategory
              </button>
              <button onClick={() => setShowTrendingUploadForm(true)} className="btn btn-secondary">
                Add Trending Upload
              </button>
            </div>
          </div>

          {/* Trending Subcategory Form Modal */}
          {showTrendingSubcategoryForm && (
            <div className="modal-overlay">
              <div className="modal">
                <div className="modal-header">
                  <h3>Create Trending Subcategory</h3>
                  <button
                    onClick={() => setShowTrendingSubcategoryForm(false)}
                    className="close-btn"
                  >
                    ×
                  </button>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); handleAddTrendingSubcategory(); }} className="trending-subcategory-form">
                  <div className="form-group">
                    <label>Subcategory Name *</label>
                    <input
                      type="text"
                      value={trendingSubcategoryFormData.name}
                      onChange={(e) => setTrendingSubcategoryFormData({ ...trendingSubcategoryFormData, name: e.target.value })}
                      required
                      placeholder="Enter subcategory name (e.g., Viral Memes)"
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Start Date *</label>
                      <input
                        type="date"
                        value={trendingSubcategoryFormData.startDate}
                        onChange={(e) => setTrendingSubcategoryFormData({ ...trendingSubcategoryFormData, startDate: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>End Date *</label>
                      <input
                        type="date"
                        value={trendingSubcategoryFormData.endDate}
                        onChange={(e) => setTrendingSubcategoryFormData({ ...trendingSubcategoryFormData, endDate: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-actions">
                    <button type="button" onClick={() => setShowTrendingSubcategoryForm(false)} className="btn btn-secondary">
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Create
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Trending Subcategories Table */}
          <div className="trending-table-container">
            <table className="trending-table">
              <thead>
                <tr>
                  <th>Subcategory</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Add Design</th>
                  <th>Preview</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {trendingSubcategories.map(subcategory => {
                  return (
                    <tr key={subcategory.id}>
                      <td>
                        <div className="subcategory-info">
                          <span className="subcategory-name">{subcategory.categoryname}</span>
                          <span className="design-count">({formatDesignCount(subcategory.designCount)})</span>
                        </div>
                      </td>
                      <td>{formatDisplayDate(subcategory.startDate)}</td>
                      <td>{formatDisplayDate(subcategory.endDate)}</td>
                      <td>
                        <button
                          onClick={() => {

                            setQuickUploadSubcategory(subcategory);
                            setQuickUploadFiles([]);
                            setShowQuickTrendingUpload(true);
                          }}
                          className="btn-icon"
                          title="Quick Upload"
                        >
                          {TABLE_ICONS.ADD}
                        </button>
                      </td>
                      <td>
                        <button
                          onClick={() => handlePreviewTrendingSubcategory(subcategory)}
                          className="btn-icon"
                        >
                          {TABLE_ICONS.VIEW}
                        </button>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={() => handleEditTrendingSubcategory(subcategory)}
                            className="btn-icon"
                          >
                            {TABLE_ICONS.EDIT}
                          </button>
                          <button
                            onClick={() => handleDeleteTrendingSubcategory(subcategory)}
                            className="btn-icon"
                          >
                            {TABLE_ICONS.DELETE}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Trending Upload Form Modal - Direct from + button */}
      {showTrendingUploadForm && selectedTrendingSubcategory && activeCollection === 'trending' && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Upload to {selectedTrendingSubcategory.name}</h3>
              <button
                onClick={() => {
                  setShowTrendingUploadForm(false);
                  setSelectedTrendingSubcategory(null);
                  setSelectedFiles([]);
                }}
                className="close-btn"
              >
                ×
              </button>
            </div>
            <div className="upload-modal-content">
              <form onSubmit={(e) => { e.preventDefault(); handleTrendingMultiUpload(selectedFiles); }} className="trending-upload-form">
                <div className="form-group">
                  <label>Upload Images *</label>
                  <div
                    className={`multi-file-upload-area ${dragActive ? 'drag-active' : ''}`}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragActive(false);
                      const files = Array.from(e.dataTransfer.files);
                      setSelectedFiles([...selectedFiles, ...files]);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragActive(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      setDragActive(false);
                    }}
                  >
                    {selectedFiles.length > 0 ? (
                      <div className="selected-files">
                        <p>{selectedFiles.length} files selected</p>
                        <div className="file-previews">
                          {selectedFiles.map((file, index) => (
                            <div key={index} className="file-preview-item">
                              {file.type.startsWith('image/') ? (
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={file.name}
                                  className="preview-thumbnail"
                                />
                              ) : (
                                <div className="file-icon">{TABLE_ICONS.FILE}</div>
                              )}
                              <span className="file-name">{file.name}</span>
                              <button
                                type="button"
                                className="remove-file-btn"
                                onClick={() => {
                                  const newFiles = selectedFiles.filter((_, i) => i !== index);
                                  setSelectedFiles(newFiles);
                                }}
                                aria-label="Remove file"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                          <div className="add-more-files">
                            <input
                              type="file"
                              accept="image/*,video/*"
                              multiple
                              onChange={(e) => {
                                const newFiles = Array.from(e.target.files);
                                setSelectedFiles([...selectedFiles, ...newFiles]);
                              }}
                              className="file-input"
                              id="additional-trending-files-quick"
                              style={{ display: 'none' }}
                            />
                            <label htmlFor="additional-trending-files-quick" className="add-more-btn">
                              + Add More
                            </label>
                          </div>
                        </div>
                        <div className="file-actions">
                          <button
                            type="button"
                            onClick={() => setSelectedFiles([])}
                            className="btn btn-sm btn-secondary"
                          >
                            Clear All
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="upload-placeholder">
                        <div className="upload-icon">{TABLE_ICONS.FOLDER}</div>
                        <p>Drag and drop multiple images/videos here, or click to select</p>
                        <input
                          type="file"
                          accept="image/*,video/*"
                          multiple
                          onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                          className="file-input"
                          style={{ display: 'none' }}
                        />
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => triggerFileInput('.file-input')}
                        >
                          Choose Files
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" onClick={() => {
                    setShowTrendingUploadForm(false);
                    setSelectedTrendingSubcategory(null);
                    setSelectedFiles([]);
                  }} className="btn btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={uploading || selectedFiles.length === 0}>
                    {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} Files`}
                  </button>
                </div>
              </form>

              {/* Uploaded Images Preview */}
              <div className="uploaded-images-section">
                <h4>Uploaded Images ({trendingUploads.length})</h4>
                <div className="uploaded-images-grid">
                  {trendingUploads.map((upload) => (
                    <div key={upload.id} className="uploaded-image-card">
                      <img src={upload.thumbnailImg || upload.url} alt={upload.name} />
                      <button
                        className="delete-upload-btn"
                        onClick={() => handleDeleteTrendingUpload(upload.id, selectedTrendingSubcategory.id, upload.url)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Trending Subcategory Form Modal */}
      {showEditTrendingSubcategoryForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit Trending Subcategory</h3>
              <button
                onClick={() => setShowEditTrendingSubcategoryForm(false)}
                className="close-btn"
              >
                ×
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handleUpdateTrendingSubcategory(); }} className="edit-trending-subcategory-form">
              <div className="form-group">
                <label>Subcategory Name *</label>
                <input
                  type="text"
                  value={trendingSubcategoryFormData.name}
                  onChange={(e) => setTrendingSubcategoryFormData({ ...trendingSubcategoryFormData, name: e.target.value })}
                  required
                  placeholder="Enter subcategory name"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    value={trendingSubcategoryFormData.startDate}
                    onChange={(e) => setTrendingSubcategoryFormData({ ...trendingSubcategoryFormData, startDate: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>End Date *</label>
                  <input
                    type="date"
                    value={trendingSubcategoryFormData.endDate}
                    onChange={(e) => setTrendingSubcategoryFormData({ ...trendingSubcategoryFormData, endDate: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowEditTrendingSubcategoryForm(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Update Subcategory
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Category Form Modal */}
      {showCategoryForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New {activeCollectionData?.label} Category</h3>
              <button
                onClick={() => setShowCategoryForm(false)}
                className="close-btn"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCategorySubmit} className="category-form">
              <div className="form-group">
                <label>Category Name *</label>
                <input
                  type="text"
                  name="name"
                  value={categoryFormData.name}
                  onChange={handleCategoryInputChange}
                  required
                  placeholder="Enter category name (e.g., Birthday)"
                />
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setShowCategoryForm(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Add Category
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Form Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingUpload ? 'Edit' : 'Add New'} {activeCollectionData?.label}</h3>
              <button
                onClick={() => setShowForm(false)}
                className="close-btn"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="upload-form">
              <div className="form-group">
                <label>Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="datetime-local"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="datetime-local"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>File *</label>
                <div
                  className={`file-upload-area ${dragActive ? 'drag-active' : ''}`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  {filePreview ? (
                    <div className="file-preview">
                      <img src={filePreview} alt="Preview" />
                      <button
                        type="button"
                        className="remove-file-btn"
                        onClick={removeFile}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <div className="upload-icon">{TABLE_ICONS.FOLDER}</div>
                      <p>Drag and drop a file here, or click to select</p>
                      <input
                        type="file"
                        accept="image/*,video/*"
                        onChange={handleFileInput}
                        className="file-input"
                        style={{ display: 'none' }}
                      />
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => triggerFileInput('.file-input')}
                      >
                        Choose File
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setShowForm(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={uploading}>
                  {uploading ? 'Uploading...' : (editingUpload ? 'Update' : 'Upload')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* General Trending Upload Form Modal - Available from main trending page */}
      {showTrendingUploadForm && activeCollection === 'trending' && !showTrendingManagement && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Upload Trending Designs</h3>
              <button
                onClick={() => setShowTrendingUploadForm(false)}
                className="close-btn"
              >
                ×
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handleGeneralTrendingUpload(selectedFiles); }} className="trending-upload-form">
              <div className="form-group">
                <label>Select Subcategory *</label>
                <select
                  value={trendingUploadFormData.subcategoryId}
                  onChange={(e) => setTrendingUploadFormData({ ...trendingUploadFormData, subcategoryId: e.target.value })}
                  required
                >
                  <option value="">Select Subcategory</option>
                  {trendingSubcategories.map(subcategory => (
                    <option key={subcategory.id} value={subcategory.id}>
                      {subcategory.categoryname}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Upload Images *</label>
                <div
                  className={`multi-file-upload-area ${dragActive ? 'drag-active' : ''}`}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragActive(false);
                    const files = Array.from(e.dataTransfer.files);
                    setSelectedFiles([...selectedFiles, ...files]);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragActive(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setDragActive(false);
                  }}
                >
                  {selectedFiles.length > 0 ? (
                    <div className="selected-files">
                      <p>{selectedFiles.length} files selected</p>
                      <div className="file-previews">
                        {selectedFiles.map((file, index) => (
                          <div key={index} className="file-preview-item">
                            {file.type.startsWith('image/') ? (
                              <img
                                src={URL.createObjectURL(file)}
                                alt={file.name}
                                className="preview-thumbnail"
                              />
                            ) : (
                              <div className="file-icon">{TABLE_ICONS.FILE}</div>
                            )}
                            <span className="file-name">{file.name}</span>
                            <button
                              type="button"
                              className="remove-file-btn"
                              onClick={() => {
                                const newFiles = selectedFiles.filter((_, i) => i !== index);
                                setSelectedFiles(newFiles);
                              }}
                              aria-label="Remove file"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        <div className="add-more-files">
                          <input
                            type="file"
                            accept="image/*,video/*"
                            multiple
                            onChange={(e) => {
                              const newFiles = Array.from(e.target.files);
                              setSelectedFiles([...selectedFiles, ...newFiles]);
                            }}
                            className="file-input"
                            id="additional-trending-files-general"
                            style={{ display: 'none' }}
                          />
                          <label htmlFor="additional-trending-files-general" className="add-more-btn">
                            + Add More
                          </label>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <div className="upload-icon">{TABLE_ICONS.FOLDER}</div>
                      <p>Drag and drop multiple images/videos here, or click to select</p>
                      <input
                        type="file"
                        accept="image/*,video/*"
                        multiple
                        onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                        className="file-input"
                        style={{ display: 'none' }}
                      />
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => triggerFileInput('.file-input')}
                      >
                        Choose Files
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setShowTrendingUploadForm(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedFiles([])}
                  className="btn btn-secondary"
                  disabled={selectedFiles.length === 0}
                >
                  Clear All
                </button>
                <button type="submit" className="btn btn-primary" disabled={uploading || selectedFiles.length === 0}>
                  {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} Files`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* General Greeting Upload Form Modal - Available from main greetings page */}
      {showGreetingUploadForm && activeCollection === 'greetings' && !showGreetingManagement && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Upload Greeting Designs</h3>
              <button
                onClick={() => setShowGreetingUploadForm(false)}
                className="close-btn"
              >
                ×
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handleGeneralGreetingUpload(selectedFiles); }} className="greeting-upload-form">
              <div className="form-group">
                <label>Select Subcategory *</label>
                <select
                  value={greetingUploadFormData.subcategoryId}
                  onChange={(e) => setGreetingUploadFormData({ ...greetingUploadFormData, subcategoryId: e.target.value })}
                  required
                >
                  <option value="">Select Subcategory</option>
                  {greetingSubcategories.map(subcategory => (
                    <option key={subcategory.id} value={subcategory.id}>
                      {subcategory.categoryname}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Upload Images *</label>
                <div
                  className={`multi-file-upload-area ${dragActive ? 'drag-active' : ''}`}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragActive(false);
                    const files = Array.from(e.dataTransfer.files);
                    setSelectedFiles([...selectedFiles, ...files]);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragActive(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setDragActive(false);
                  }}
                >
                  {selectedFiles.length > 0 ? (
                    <div className="selected-files">
                      <p>{selectedFiles.length} files selected</p>
                      <div className="file-previews">
                        {selectedFiles.map((file, index) => (
                          <div key={index} className="file-preview-item">
                            {file.type.startsWith('image/') ? (
                              <img
                                src={URL.createObjectURL(file)}
                                alt={file.name}
                                className="preview-thumbnail"
                              />
                            ) : (
                              <div className="file-icon">{TABLE_ICONS.FILE}</div>
                            )}
                            <span className="file-name">{file.name}</span>
                            <button
                              type="button"
                              className="remove-file-btn"
                              onClick={() => {
                                const newFiles = selectedFiles.filter((_, i) => i !== index);
                                setSelectedFiles(newFiles);
                              }}
                              aria-label="Remove file"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        <div className="add-more-files">
                          <input
                            type="file"
                            accept="image/*,video/*"
                            multiple
                            onChange={(e) => {
                              const newFiles = Array.from(e.target.files);
                              setSelectedFiles([...selectedFiles, ...newFiles]);
                            }}
                            className="file-input"
                            id="additional-greeting-files"
                            style={{ display: 'none' }}
                          />
                          <label htmlFor="additional-greeting-files" className="add-more-btn">
                            + Add More
                          </label>
                        </div>
                      </div>
                      <div className="file-actions">
                        <button
                          type="button"
                          onClick={() => setSelectedFiles([])}
                          className="btn btn-sm btn-secondary"
                        >
                          Clear All
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <div className="upload-icon">{TABLE_ICONS.FOLDER}</div>
                      <p>Drag and drop multiple images/videos here, or click to select</p>
                      <input
                        type="file"
                        accept="image/*,video/*"
                        multiple
                        onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                        className="file-input"
                        style={{ display: 'none' }}
                      />
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => triggerFileInput('.file-input')}
                      >
                        Choose Files
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setShowGreetingUploadForm(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={uploading || selectedFiles.length === 0}>
                  {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} Files`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quick Trending Upload Modal */}
      {showQuickTrendingUpload && quickUploadSubcategory && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Quick Upload to {quickUploadSubcategory.name}</h3>
              <button
                onClick={() => {
                  setShowQuickTrendingUpload(false);
                  setQuickUploadSubcategory(null);
                  setQuickUploadFiles([]);
                }}
                className="close-btn"
              >
                ×
              </button>
            </div>
            <div className="upload-modal-content">
              <form onSubmit={(e) => { e.preventDefault(); handleQuickTrendingUpload(); }} className="quick-upload-form">
                <div className="form-group">
                  <label>Upload Images *</label>
                  <div
                    className={`multi-file-upload-area ${dragActive ? 'drag-active' : ''}`}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragActive(false);
                      const files = Array.from(e.dataTransfer.files);
                      setQuickUploadFiles([...quickUploadFiles, ...files]);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragActive(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      setDragActive(false);
                    }}
                  >
                    {quickUploadFiles.length > 0 ? (
                      <div className="selected-files">
                        <p>{quickUploadFiles.length} files selected</p>
                        <div className="file-previews">
                          {quickUploadFiles.map((file, index) => (
                            <div key={index} className="file-preview-item">
                              {file.type.startsWith('image/') ? (
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={file.name}
                                  className="preview-thumbnail"
                                />
                              ) : (
                                <div className="file-icon">{TABLE_ICONS.FILE}</div>
                              )}
                              <span className="file-name">{file.name}</span>
                              <button
                                type="button"
                                className="remove-file-btn"
                                onClick={() => {
                                  const newFiles = quickUploadFiles.filter((_, i) => i !== index);
                                  setQuickUploadFiles(newFiles);
                                }}
                                aria-label="Remove file"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                          <div className="add-more-files">
                            <input
                              type="file"
                              accept="image/*,video/*"
                              multiple
                              onChange={(e) => {
                                const newFiles = Array.from(e.target.files);
                                setQuickUploadFiles([...quickUploadFiles, ...newFiles]);
                              }}
                              className="file-input"
                              id="additional-quick-trending-files"
                              style={{ display: 'none' }}
                            />
                            <label htmlFor="additional-quick-trending-files" className="add-more-btn">
                              + Add More
                            </label>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="upload-placeholder">
                        <div className="upload-icon">{TABLE_ICONS.FOLDER}</div>
                        <p>Drag and drop multiple images/videos here, or click to select</p>
                        <input
                          type="file"
                          accept="image/*,video/*"
                          multiple
                          onChange={(e) => setQuickUploadFiles(Array.from(e.target.files))}
                          className="file-input"
                          style={{ display: 'none' }}
                        />
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => triggerFileInput('.file-input')}
                        >
                          Choose Files
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" onClick={() => {
                    setShowQuickTrendingUpload(false);
                    setQuickUploadSubcategory(null);
                    setQuickUploadFiles([]);
                  }} className="btn btn-secondary">
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickUploadFiles([])}
                    className="btn btn-secondary"
                    disabled={quickUploadFiles.length === 0}
                  >
                    Clear All
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={uploading || quickUploadFiles.length === 0}>
                    {uploading ? 'Uploading...' : `Upload ${quickUploadFiles.length} Files`}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Quick Today+Tomorrow Upload Modal */}
      {showQuickTodayTomorrowUpload && quickUploadSubcategory && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Quick Upload to {quickUploadSubcategory.categoryname}</h3>
              <button
                onClick={() => {
                  setShowQuickTodayTomorrowUpload(false);
                  setQuickUploadSubcategory(null);
                  setQuickUploadFiles([]);
                }}
                className="close-btn"
              >
                ×
              </button>
            </div>
            <div className="upload-modal-content">
              <form onSubmit={(e) => { e.preventDefault(); handleQuickTodayTomorrowUpload(); }} className="quick-upload-form">
                <div className="form-group">
                  <label>Upload Images *</label>
                  <div
                    className={`multi-file-upload-area ${dragActive ? 'drag-active' : ''}`}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragActive(false);
                      const files = Array.from(e.dataTransfer.files);
                      setQuickUploadFiles([...quickUploadFiles, ...files]);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragActive(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      setDragActive(false);
                    }}
                  >
                    {quickUploadFiles.length > 0 ? (
                      <div className="selected-files">
                        <p>{quickUploadFiles.length} files selected</p>
                        <div className="file-previews">
                          {quickUploadFiles.map((file, index) => (
                            <div key={index} className="file-preview-item">
                              {file.type.startsWith('image/') ? (
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={file.name}
                                  className="preview-thumbnail"
                                />
                              ) : (
                                <div className="file-icon">{TABLE_ICONS.FILE}</div>
                              )}
                              <span className="file-name">{file.name}</span>
                              <button
                                type="button"
                                className="remove-file-btn"
                                onClick={() => {
                                  const newFiles = quickUploadFiles.filter((_, i) => i !== index);
                                  setQuickUploadFiles(newFiles);
                                }}
                                aria-label="Remove file"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                          <div className="add-more-files">
                            <input
                              type="file"
                              accept="image/*,video/*"
                              multiple
                              onChange={(e) => {
                                const newFiles = Array.from(e.target.files);
                                setQuickUploadFiles([...quickUploadFiles, ...newFiles]);
                              }}
                              className="file-input"
                              id="additional-quick-todaytomorrow-files"
                              style={{ display: 'none' }}
                            />
                            <label htmlFor="additional-quick-todaytomorrow-files" className="add-more-btn">
                              + Add More
                            </label>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="upload-placeholder">
                        <div className="upload-icon">{TABLE_ICONS.FOLDER}</div>
                        <p>Drag and drop multiple images/videos here, or click to select</p>
                        <input
                          type="file"
                          accept="image/*,video/*"
                          multiple
                          onChange={(e) => setQuickUploadFiles(Array.from(e.target.files))}
                          className="file-input"
                          style={{ display: 'none' }}
                        />
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => triggerFileInput('.file-input')}
                        >
                          Choose Files
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" onClick={() => {
                    setShowQuickTodayTomorrowUpload(false);
                    setQuickUploadSubcategory(null);
                    setQuickUploadFiles([]);
                  }} className="btn btn-secondary">
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickUploadFiles([])}
                    className="btn btn-secondary"
                    disabled={quickUploadFiles.length === 0}
                  >
                    Clear All
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={uploading || quickUploadFiles.length === 0}>
                    {uploading ? 'Uploading...' : `Upload ${quickUploadFiles.length} Files`}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar */}
      {snackbar.show && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={hideSnackbar}
        />
      )}
    </div>
  );
};

export default Uploads;
